import { useState, useEffect, useLayoutEffect, useCallback, useRef, forwardRef, useImperativeHandle, useMemo, type ComponentType } from 'react'
import {
  ChevronRight,
  ChevronDown,
  Loader2,
  Link2,
  Unlink,
  Upload,
  FolderUp,
  Pencil,
  Trash2,
  Copy,
  Scissors,
  ClipboardPaste,
  Bot,
  Search,
  X,
  ExternalLink,
  AlertTriangle,
  Users,
  Settings,
  Play,
  Bug,
  Palette,
} from 'lucide-react'
import { AIOverrideBadge } from './AIOverrideBadge'
import { useNavigate } from 'react-router-dom'
import { usePendingPointerGate, usePendingPointerBlock, PendingPointerGateProvider, isPendingPointer, type PendingPointerLike } from '@papan/files/ui/pending-pointer-gate'
import { shareLinkOffered, createSharePathLookups, linkedRootShareRow, type RootShareFacts } from './shareLinkMenu'
import { markTreeStale, markListCacheStale, loadedFolderIds, readTreeFolder, useLatestFolderLoads, type FolderLoad } from './treeStale'
import { pointerFactsFromListing, type ContextMenuRow } from './listingRow'
import { CodeApprovalBadge, CodeApprovalProvider, type CodeApprovalRow } from './codeApproval'
import { useQueryClient } from '@tanstack/react-query'
import { getActiveTenantId, getActiveOrgEmail, isSessionEndedError, pinnedBatch } from '@papan/api-client'
import { filesApi, onListedRowRefused, type FileListItem } from '@papan/files/client'
import { isMiniappFolder, supportsDebugLog, isSharedPointer, AI_ACCESS_SUSPENDED, enforcedAIOverride } from '@papan/types'
import { fileKeys, fetchAndCacheFileInfo, IdentityChangedError, useFilesList, type ListQueryOptions } from '@papan/files/client/queries'
import { createDragImage, setActiveDragFolderIds, clearActiveDrag, isCircularMove, useFolderAncestors, offerDragToApps } from '@papan/files/client/dnd'
import { startPapanDrag, isPapanDrag, readPapanDrag, peekPapanDragFolderIds, type PapanDragPayload } from '@papan/core-ui/ui/papanDrag'
import { useToast, useConfirm, useUndo, useConflict, FileIcon, useCommandScope, useCommandRegistryOptional, formatShortcut, FileContextMenu, type FileMenuItem, type Command, miniappIconProps, fileIconProps, useSwapScroll, useWhenShown } from '@papan/core-ui/ui'
import { captureUndoContext } from '@papan/core-ui/ui/undoCapture'
import { areExtensionsCompatible, requestMiniappView } from '@papan/collab/client'
import { collectDroppedFiles } from '@papan/files/client/dropUpload'
import { useUploadService, WhoHasAccessDialog, DebugLogViewer, MiniappIconEditorDialog } from '@papan/files/ui'
import { ShareLinksDialog } from '@papan/share-links/ui'
import { shareKindForFile } from '@papan/share-links/client'
import { moveResultMessage, isMountId } from '@papan/files/ui/protected-branches'
import { moveErrorText } from '@papan/files/ui/move-error-text'
import { clearSpentCut } from '@papan/files/ui/spent-cut'
import { withMoveProgress } from '@papan/files/ui/move-progress'
import { pasteItems, pasteUndoRecord } from '@papan/files/client/paste'
import { pasteToastText } from '@papan/files/ui/paste-toast'
import { useRealtime } from '@papan/realtime/client'
import { createAccessBurst, changesViewerAccess } from '@papan/types'
import { useDeleteRunner, useApproveCode } from '@papan/files/ui'
import { useTranslation } from '@papan/i18n'
import type { WorkspaceLink } from '@papan/workspace/types'
import { LinkedFolderTree } from './LinkedFolderTree'
import { FolderPickerDialog, isRootFolderId } from './FolderPickerDialog'
import { waitForElement } from './revealPoll'

interface FileNode {
  id: string
  name: string
  isFolder: boolean
  size: number
  parentId: string | null
  children?: FileNode[]
  isLoaded?: boolean
  isLoading?: boolean
  /**
   * The last load of this folder's children failed (#1058 code review). The
   * row's AUTOMATIC loads skip it: they depend on `isLoading`, so a failure
   * (isLoading true → false, still not loaded) re-fired them in a loop, and a
   * resync marks every expanded folder stale at once. A click (on a collapsed
   * row: clicking an expanded one collapses it, the next click retries), a
   * reveal into it, or a resync (`markTreeStale` clears it) asks again; a
   * listing merge keeps it (else every realtime patch of the parent would
   * re-ask). Set only by the folder's LATEST load, and only while the folder
   * has no loaded rows at that moment (`isLoaded` false) — kept rows from
   * before may still be shown under it.
   */
  loadFailed?: boolean
  /** Folder row's metadata_json — carries the miniappIcon spec. */
  metadataJson?: string | null
  /** DB row-kind column + the D17 approval flag — `fileIconProps` draws the pointer
   *  badge and greys a `.chain` that still needs approval. */
  fileType?: string | null
  /** Code-file approval (#1452): Approve on this row (the content no person set); `contentHash` is what it names. */
  codeNeedsApproval?: boolean
  codeCanApprove?: boolean
  /** A folder with a code file needing approval below it — a warning on the folder row. */
  codeApprovalBelow?: boolean
  contentHash?: string | null
  chainNeedsApproval?: boolean
  chainCanApprove?: boolean
  chainBroken?: boolean
  /** The viewer's permission on this row as the SERVER resolved it for the listing — the app-reach
   *  cap included (inside a PDApp they can't edit, everything is read-only). Drives the lock. */
  effectivePermission?: string
  /** The owner shared this pointer WITHOUT onward-sharing rights, so minting a link on it
   *  is refused (`reshare_blocked_by_grant`). The listing sends it; the drive reads it the
   *  same way (`useFiles`) and greys its share actions on it (#327). */
  reshareBlocked?: boolean
  /** The pointer is dead — revoked, or the target is gone. A mint on it is a hard
   *  403 (`shareLinkOffered` has the measurement), so the share item is withheld. */
  shareBroken?: boolean
}

interface ClipboardData {
  items: { id: string; name: string; isFolder: boolean; parentId: string | null }[]
  isCut: boolean
  /** (#752) The tenant the items live in. The clipboard now survives a tenant
   *  switch, so every writer stamps it and every reader checks it. */
  tenantId?: string
  orgEmail?: string
}

/** Stamp the active tenant + identity, as DriveView does — the drive's
 *  cross-tenant paste reads them to resolve the source side. */
function stampClipboard(items: ClipboardData['items'], isCut: boolean): ClipboardData {
  return { items, isCut, tenantId: getActiveTenantId() || undefined, orgEmail: getActiveOrgEmail() || undefined }
}

const CLIPBOARD_KEY = 'papan-clipboard'

interface SelectionEntry {
  name: string
  isFolder: boolean
  parentId: string | null
  parentName: string | null
  scope: string | null  // linkId or null for workspace files
}

interface ContextMenuState {
  x: number
  y: number
  /** The folder ID context — either a clicked folder or the workspace root */
  targetFolderId: string | null
  /** The specific item that was right-clicked (for rename) */
  itemId?: string
  itemName?: string
  itemIsFolder?: boolean
  itemParentId?: string | null
  itemParentName?: string | null
  /** The right-clicked row's pointer facts — a chain that Needs approval doesn't open (I114). */
  itemPointer?: PendingPointerLike
  /** The right-clicked row's code-file approval facts (#1452) — the menu offers Approve. */
  itemCode?: CodeApprovalRow
  /** The row was shared to this org without onward-sharing rights — minting a link on it
   *  is refused by the server, so it is not offered. */
  itemReshareBlocked?: boolean
  /** The row is a dead pointer — a mint on it is a hard 403. */
  itemShareBroken?: boolean
  /**
   * Set when the row's share facts are NOT KNOWN here, which makes the share gate
   * refuse rather than guess. Only the ROOT row of a "Linked from Drives" section,
   * and only until its children have been loaded: the root is lazy, and it is the
   * inbound mount itself, so it is the likeliest row of all to be reshare-blocked.
   *
   * It used to be set unconditionally, on the stated grounds that getting the facts
   * "is a server change". That was wrong — the listing that loads the root's
   * children already answers with `mount.reshareBlocked`, the same field the drive
   * reads for the row you are standing in, and `LinkedFolderTree` was already
   * reading `parentPermission` off that very response. It passes the facts up now,
   * and this flag covers only the window before that load.
   */
  itemShareFactsUnknown?: boolean
  /**
   * Set when the menu was opened on a SECTION rather than on an item — the
   * empty space below the tree, or either section header. Which section
   * matters: every create/paste/upload action targets "In this Project", so
   * offering them from the "Linked from Drives" header would act on a section
   * the user is not pointing at.
   */
  section?: 'files' | 'links'
  /** Set when right-clicking a root linked folder — enables Unlink option */
  linkId?: string
  /** Which linked tree this item belongs to (for scoped rename) */
  scope?: string | null
  /**
   * Snapshot of the effective selection when the menu opened. The menu's
   * gating, labels, and actions read THIS — not the live `selection` — because
   * a mousedown on the portaled menu triggers the click-outside handler that
   * clears `selection` before the item's click fires. Reading the snapshot
   * keeps the menu stable (no item add/remove → no DOM churn that detaches the
   * button mid-click) and its actions correct.
   */
  selEntries?: Array<[string, SelectionEntry]>
}


/** Match text against a pattern that supports `*` wildcards.
 *  Without `*`, falls back to substring includes. */
// Filter utilities shared with LinkedFolderTree and FilePickerDialog
import { pathMatchesFilter, nodeMatchesFilter, sortFileItems } from './fileFilter'

function WorkspaceFileItem({
  item,
  depth,
  selectedIds,
  activeFileId,
  autoExpandIds,
  renamingId,
  draggedIds,
  dropTargetId,
  forbiddenDropId,
  aiOverrideMap,
  inheritedOverride,
  userPermMap,
  inheritedUserPerm,
  onSelect,
  onFileOpen,
  onLoadChildren,
  onContextMenu,
  onRenameCommit,
  onRenameCancel,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  filterText,
  filterAnchored,
  filterPathPrefix,
}: {
  item: FileNode
  depth: number
  selectedIds: ReadonlySet<string>
  activeFileId: string | null
  autoExpandIds: Set<string> | null
  renamingId: string | null
  draggedIds: ReadonlySet<string>
  dropTargetId: string | null
  forbiddenDropId: string | null
  aiOverrideMap?: Map<string, 'hidden' | 'r'>
  inheritedOverride?: 'hidden' | 'r'
  userPermMap?: Map<string, string>
  inheritedUserPerm?: string
  onSelect: (id: string, name: string, isFolder: boolean, e: React.MouseEvent) => void
  onFileOpen: (fileId: string, name?: string) => void
  onLoadChildren: (folderId: string) => void
  onContextMenu: (item: FileNode, e: React.MouseEvent) => void
  onRenameCommit: (id: string, newName: string, currentName?: string) => void
  onRenameCancel: () => void
  onDragStart: (e: React.DragEvent, item: FileNode) => void
  onDragOver: (e: React.DragEvent, item: FileNode) => void
  onDragLeave: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, item: FileNode) => void
  onDragEnd: () => void
  filterText?: string
  filterAnchored?: boolean
  filterPathPrefix?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const blockPendingPointer = usePendingPointerBlock()
  const isRenaming = renamingId === item.id
  const [renameValue, setRenameValue] = useState(item.name)
  const renameInputRef = useRef<HTMLInputElement>(null)

  // Auto-expand folders only when the folder itself doesn't match but children do
  // (expand to reveal matching files, not just because the folder name matches)
  const prevFilterRef = useRef(filterText)
  useEffect(() => {
    if (filterText && filterText !== prevFilterRef.current && item.isFolder) {
      const folderPath = (filterPathPrefix || '/') + item.name.toLowerCase() + '/'
      const folderSelfMatches = pathMatchesFilter(folderPath, filterText, !!filterAnchored)
      if (!folderSelfMatches) {
        setExpanded(true)
      }
    }
    if (!filterText) setExpanded(false)
    prevFilterRef.current = filterText
  }, [filterText, item.isFolder, item.name, filterPathPrefix, filterAnchored])

  // Focus and select name (without extension) when entering rename mode
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus()
      const dotIdx = item.name.lastIndexOf('.')
      if (dotIdx > 0 && !item.isFolder) {
        renameInputRef.current.setSelectionRange(0, dotIdx)
      } else {
        renameInputRef.current.select()
      }
      setRenameValue(item.name)
    }
  }, [isRenaming, item.name, item.isFolder])

  // Auto-re-fetch children when folder is expanded but data was reset (e.g. after parent refresh).
  // Not a folder whose last load failed (`FileNode.loadFailed`): it would re-ask in a loop.
  useEffect(() => {
    if (expanded && item.isFolder && !item.isLoaded && !item.isLoading && !item.loadFailed) {
      onLoadChildren(item.id)
    }
  }, [expanded, item.isFolder, item.isLoaded, item.isLoading, item.loadFailed, item.id, onLoadChildren])

  // Auto-expand when parent requests reveal. A reveal into a folder whose last
  // load failed asks again, ONCE per reveal (it waits for a row inside it; a
  // folder that keeps failing must not be re-asked in a loop) — #1058.
  const revealRetriedRef = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (!autoExpandIds?.has(item.id) || !item.isFolder) return
    if (!expanded) setExpanded(true)
    if (item.loadFailed && !item.isLoading && revealRetriedRef.current !== autoExpandIds) {
      revealRetriedRef.current = autoExpandIds
      onLoadChildren(item.id)
    }
  }, [autoExpandIds, item.id, item.isFolder, expanded, item.loadFailed, item.isLoading, onLoadChildren])

  // Auto-load children when filter is active but subfolder children haven't been fetched
  useEffect(() => {
    if (filterText && item.isFolder && !item.isLoaded && !item.isLoading && !item.loadFailed) {
      onLoadChildren(item.id)
    }
  }, [filterText, item.isFolder, item.isLoaded, item.isLoading, item.loadFailed, item.id, onLoadChildren])

  const handleClick = (e: React.MouseEvent) => {
    onSelect(item.id, item.name, item.isFolder, e)
    // Only open file / toggle folder on plain click (no modifier)
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
      // D17 / I114 — a chain that Needs approval doesn't open or expand.
      if (blockPendingPointer(item)) return
      if (item.isFolder) {
        if (!expanded && !item.isLoaded) {
          onLoadChildren(item.id)
        }
        setExpanded(!expanded)
      } else {
        onFileOpen(item.id, item.name)
      }
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // If right-clicked item is already in selection, keep selection; otherwise replace
    if (!selectedIds.has(item.id)) {
      onSelect(item.id, item.name, item.isFolder, e)
    }
    onContextMenu(item, e)
  }

  const commitRename = () => {
    const trimmed = renameValue.trim()
    if (trimmed) {
      onRenameCommit(item.id, trimmed, item.name)
    } else {
      onRenameCancel()
    }
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitRename()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onRenameCancel()
    }
  }

  const isDragging = draggedIds.has(item.id)
  const isDropTarget = dropTargetId === item.id
  const isForbidden = forbiddenDropId === item.id

  // Effective override: own override wins if more restrictive, otherwise inherit from parent
  const ownOverride = aiOverrideMap?.get(item.id)
  const effectiveOverride = ownOverride === 'hidden' || inheritedOverride === 'hidden'
    ? 'hidden' as const
    : ownOverride || inheritedOverride

  // User permission: own entry wins, otherwise inherit from parent
  const ownUserPerm = userPermMap?.get(item.id)
  // The server's per-row answer wins (it knows the app-reach cap — owner, 2026-10-03: a PDApp you
  // can't edit shows its files locked); the explicit-grant map + role default are the fallback.
  const effectiveUserPerm = item.effectivePermission || ownUserPerm || inheritedUserPerm
  const userReadOnly = effectiveUserPerm ? !['admin', 'readwrite', 'owner'].includes(effectiveUserPerm) : false

  // Filter: hide items that don't match (after all hooks to comply with Rules of Hooks)
  if (filterText && !nodeMatchesFilter(item, filterText, filterPathPrefix || '/', !!filterAnchored)) return null

  return (
    <>
      <div
        data-file-id={item.id}
        data-file-name={item.name}
        data-file-is-folder={item.isFolder ? '1' : '0'}
        data-file-parent-id={item.parentId ?? ''}
        data-file-scope=""
        draggable={!isRenaming}
        onClick={isRenaming ? undefined : handleClick}
        onContextMenu={handleContextMenu}
        onDragStart={(e) => onDragStart(e, item)}
        onDragOver={(e) => item.isFolder && onDragOver(e, item)}
        onDragLeave={onDragLeave}
        onDrop={(e) => item.isFolder && onDrop(e, item)}
        onDragEnd={onDragEnd}
        className={`group flex items-center gap-1 w-full text-left py-1 px-1 rounded text-sm cursor-pointer ${
          isForbidden
            ? 'bg-red-600/20 border border-red-500/50'
            : isDropTarget
            ? 'bg-green-600/30 border border-green-500'
            : isDragging
              ? 'opacity-50'
              : selectedIds.has(item.id)
                ? 'bg-brand-500/20 themed-text'
                : activeFileId === item.id
                  ? 'themed-bg-tertiary themed-text'
                  : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'
        }`}
        style={{ paddingLeft: `${depth * 12 + 4}px`, ...(enforcedAIOverride(effectiveOverride) === 'hidden' && !(ownOverride === 'hidden' && inheritedOverride !== 'hidden') ? { opacity: 0.3 } : {}) }}
      >
        {item.isFolder ? (
          item.isLoading ? (
            <Loader2 size={14} className="animate-spin shrink-0" />
          ) : expanded ? (
            <ChevronDown size={14} className="shrink-0" />
          ) : (
            <ChevronRight size={14} className="shrink-0" />
          )
        ) : (
          <span className="w-[5px] shrink-0" />
        )}
        <FileIcon {...fileIconProps({ ...item, name: isRenaming ? renameValue : item.name })} {...miniappIconProps(item)} miniappIconName={isRenaming ? renameValue : item.name} />
        {isRenaming ? (
          <input
            ref={renameInputRef}
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={commitRename}
            className="flex-1 min-w-0 bg-transparent border themed-border rounded px-1 py-0 text-sm themed-text outline-none focus:border-brand-500"
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <>
            <span className="truncate">{item.name}</span>
            <CodeApprovalBadge row={item} />
            <AIOverrideBadge override={effectiveOverride} userReadOnly={userReadOnly} />
            {item.isFolder && isMiniappFolder(item.name) && (
              <span className="ml-auto flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); requestMiniappView(item.id, 'run'); onFileOpen(item.id, item.name) }}
                  className="p-0.5 themed-text-muted hover:themed-text"
                  data-tooltip="Preview app"
                  aria-label="Preview app"
                >
                  <Play size={13} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); requestMiniappView(item.id, 'settings'); onFileOpen(item.id, item.name) }}
                  className="p-0.5 themed-text-muted hover:themed-text"
                  data-tooltip="Mini app settings"
                  aria-label="Mini app settings"
                >
                  <Settings size={13} />
                </button>
              </span>
            )}
          </>
        )}
      </div>
      {item.isFolder && expanded && item.children && (
        <div
          data-children-of={item.id}
          onDragOver={(e) => {
            e.preventDefault()
            onDragOver(e, item)
          }}
          onDragLeave={onDragLeave}
          onDrop={(e) => {
            e.preventDefault()
            onDrop(e, item)
          }}
        >
          {item.children.map(child => (
            <WorkspaceFileItem
              key={child.id}
              item={child}
              depth={depth + 1}
              selectedIds={selectedIds}
              activeFileId={activeFileId}
              autoExpandIds={autoExpandIds}
              renamingId={renamingId}
              draggedIds={draggedIds}
              dropTargetId={dropTargetId}
              forbiddenDropId={forbiddenDropId}
              aiOverrideMap={aiOverrideMap}
              inheritedOverride={effectiveOverride}
              userPermMap={userPermMap}
              inheritedUserPerm={effectiveUserPerm}
              onSelect={onSelect}
              onFileOpen={onFileOpen}
              onLoadChildren={onLoadChildren}
              onContextMenu={onContextMenu}
              onRenameCommit={onRenameCommit}
              onRenameCancel={onRenameCancel}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              filterText={filterText}
              filterAnchored={filterAnchored}
              filterPathPrefix={(filterPathPrefix || '/') + item.name.toLowerCase() + '/'}
            />
          ))}
        </div>
      )}
    </>
  )
}

export interface WorkspaceExplorerHandle {
  copy: () => void
  cut: () => void
  paste: () => void
  delete: () => void
  rename: () => void
  createFolder: () => void
  newFile: () => void
  newFileQuick: () => void
  prepareUpload: () => void
  linkFolder: () => void
  getSelectedName: () => string | null
  getSelectedCount: () => number
  getSelectedNames: () => string[]
  getSelectedIds: () => string[]
  getSelectedItems: () => Array<{ id: string; isFolder: boolean }>
  getPasteTargetName: () => string | null
  getDeleteLabel: () => string
  /** Reveals the file in the explorer. Returns true if it was in-context (under
   *  the workspace root or a linked folder); false if out-of-context (no known
   *  tree) — callers fall back to a drive view. */
  revealFile: (fileId: string) => Promise<boolean>
  focus: () => void
}

interface WorkspaceExplorerProps {
  tenantId: string
  /** Active tenant display name — roots the promote breadcrumb at the local drive
   *  title (matches DriveView), instead of the server's driveName which falls back
   *  to a generic "My Drive" that would mislabel an org drive. */
  tenantName?: string
  /** Container kind — names the files section ("In this Project" vs "In this PDApp"). */
  entityKind?: 'project' | 'miniapp'
  folderId: string | null
  links: WorkspaceLink[]
  activeFileId?: string | null
  /** When true, highlight the workspace files section (e.g. editor area drop) */
  externalDropHighlight?: boolean
  /** Per-file AI access overrides */
  aiOverrideMap?: Map<string, 'hidden' | 'r'>
  onFileOpen: (fileId: string, name?: string) => void
  onAddLink: (targetFolderId: string, alias?: string) => void
  onRemoveLink: (linkId: string) => void
  onFileDeleted?: (fileId: string, opts: { isFolder: boolean; path?: string }) => void
  onEnsureWorkspace?: () => Promise<string | null>
  /** Signal that explorer is busy with an async operation (e.g. upload after ensureWorkspace) */
  onBusyChange?: (busy: boolean) => void
  onSetAIAccess?: (fileId: string, permission: 'hidden' | 'r') => void
  onClearAIAccess?: (fileId: string) => void
  onMissingLinkClick?: () => void
  canWrite?: boolean
  canCopy?: boolean
  userPermMap?: Map<string, string>
  /** Bump to force reload file listings (e.g. permission change hides/shows folders) */
  accessRefreshKey?: number
}

export const WorkspaceExplorer = forwardRef<WorkspaceExplorerHandle, WorkspaceExplorerProps>(function WorkspaceExplorer({
  tenantId,
  tenantName,
  entityKind,
  folderId,
  links,
  activeFileId,
  externalDropHighlight,
  aiOverrideMap,
  userPermMap,
  onFileOpen,
  onFileDeleted,
  onAddLink,
  onRemoveLink,
  onEnsureWorkspace,
  onBusyChange,
  onSetAIAccess,
  onClearAIAccess,
  onMissingLinkClick,
  canWrite = true,
  canCopy = true,
  accessRefreshKey,
}: WorkspaceExplorerProps, ref) {
  // Role-based default permission: used as inherited perm for items not in userPermMap
  // canWrite already accounts for folder-level permissions (resolved server-side)
  const roleDefaultPerm = canWrite ? undefined : 'readcopy'
  const urlPrefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
  const routerNavigate = useNavigate()
  const queryClient = useQueryClient()
  // Tree state is *derived* from the shared TanStack Query cache: a single
  // useFilesList subscription drives the top-level Files section, and an effect
  // below merges the cache result into `workspaceFiles` (preserving any
  // already-expanded sub-children so they don't unmount on refetch).
  // Realtime mutations call `queryClient.invalidateQueries` for the affected
  // parent — the subscription auto-refetches and the merged tree updates.
  const [workspaceFiles, setWorkspaceFiles] = useState<FileNode[]>([])
  const [showFolderPicker, setShowFolderPicker] = useState(false)
  const [filterText, setFilterText] = useState('')
  const filterInputRef = useRef<HTMLInputElement>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)
  const [whoHasAccessTarget, setWhoHasAccessTarget] = useState<{ id: string; name: string; isFolder: boolean } | null>(null)
  /** The row whose share links are open. `path` is fetched rather than built
   *  from the tree: this view has no drive-root breadcrumbs, and the dialog
   *  shows the path so the person can see WHAT they are about to hand out. */
  const [shareLinkTarget, setShareLinkTarget] = useState<{ id: string; name: string; isFolder: boolean; path: string } | null>(null)
  /** Orders the path lookups — see the Share link… handler. */
  const sharePathLookups = useRef(createSharePathLookups())
  const [debugLogTarget, setDebugLogTarget] = useState<{ id: string; name: string; isFolder: boolean } | null>(null)
  const [iconEditorTarget, setIconEditorTarget] = useState<{ id: string; name: string; metadataJson?: string | null } | null>(null)
  // Track newly created item awaiting rename — if rename is cancelled, delete it
  const pendingNewItemRef = useRef<{ id: string; isFolder: boolean } | null>(null)
  const untitledCounterRef = useRef(1)
  const folderCounterRef = useRef(1)
  // Multi-selection state: Map from ID to metadata
  const [selection, setSelection] = useState<Map<string, SelectionEntry>>(new Map())
  const selectedIds = useMemo(() => new Set(selection.keys()), [selection])
  const lastSelectedId = useRef<string | null>(null)

  // DOM helpers for range selection
  const getVisibleItemIds = useCallback((): string[] => {
    if (!containerRef.current) return []
    return Array.from(containerRef.current.querySelectorAll('[data-file-id]'))
      .map(el => el.getAttribute('data-file-id')!)
  }, [])

  const getItemMetaFromDOM = useCallback((id: string): SelectionEntry | null => {
    if (!containerRef.current) return null
    const el = containerRef.current.querySelector(`[data-file-id="${id}"]`)
    if (!el) return null
    return {
      name: el.getAttribute('data-file-name') ?? '',
      isFolder: el.getAttribute('data-file-is-folder') === '1',
      parentId: el.getAttribute('data-file-parent-id') || null,
      parentName: el.getAttribute('data-file-parent-name') || null,
      scope: el.getAttribute('data-file-scope') || null,
    }
  }, [])

  const handleSelect = useCallback((
    id: string,
    scope?: string | null,
    name?: string,
    isFolder?: boolean,
    e?: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean },
    parentId?: string | null,
    parentName?: string | null,
  ) => {
    const meta: SelectionEntry = {
      name: name ?? '',
      isFolder: isFolder ?? false,
      parentId: parentId ?? null,
      parentName: parentName ?? null,
      scope: scope ?? null,
    }

    setSelection(prev => {
      // Shift+Click: range select
      if (e?.shiftKey && lastSelectedId.current) {
        const allIds = getVisibleItemIds()
        const startIdx = allIds.indexOf(lastSelectedId.current)
        const endIdx = allIds.indexOf(id)
        if (startIdx >= 0 && endIdx >= 0) {
          const [lo, hi] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx]
          const next = new Map(e.ctrlKey || e.metaKey ? prev : [])
          for (let i = lo; i <= hi; i++) {
            const itemId = allIds[i]
            if (!next.has(itemId)) {
              next.set(itemId, getItemMetaFromDOM(itemId) ?? meta)
            }
          }
          return next
        }
      }

      // Ctrl/Cmd+Click: toggle
      if (e?.ctrlKey || e?.metaKey) {
        const next = new Map(prev)
        if (next.has(id)) next.delete(id)
        else next.set(id, meta)
        return next
      }

      // Plain click: replace selection
      return new Map([[id, meta]])
    })

    // Don't update anchor on shift-click — preserve the original anchor for range selection
    if (!e?.shiftKey) {
      lastSelectedId.current = id
    }
    containerRef.current?.focus()
  }, [getVisibleItemIds, getItemMetaFromDOM])
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renamingScope, setRenamingScope] = useState<string | null>(null) // linkId or null for workspace files
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const [aiSubmenuOpen, setAiSubmenuOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [showUploadMenu, setShowUploadMenu] = useState(false)
  const uploadMenuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!showUploadMenu) return
    const onDocClick = (e: MouseEvent) => {
      if (uploadMenuRef.current && !uploadMenuRef.current.contains(e.target as Node)) {
        setShowUploadMenu(false)
      }
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [showUploadMenu])
  const uploadTargetRef = useRef<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // #897 — the tree keeps its place across a phone/desktop layout swap.
  useSwapScroll(folderId ? `explorer:${folderId}` : null, containerRef, { items: '[data-file-id]', fraction: false })
  // Pre-fetch ancestor chain for workspace root — used by centralized circular move check
  const wsAncestorIds = useFolderAncestors(folderId)
  const toast = useToast()
  const confirm = useConfirm()
  const undo = useUndo()
  const { t } = useTranslation('files')
  const resolveConflict = useConflict()

  // Clipboard state shared with DriveView via localStorage
  const [clipboard, setClipboardState] = useState<ClipboardData | null>(() => {
    try {
      const stored = localStorage.getItem(CLIPBOARD_KEY)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  })

  const setClipboard = useCallback((value: ClipboardData | null) => {
    setClipboardState(value)
    if (value) {
      localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(value))
    } else {
      localStorage.removeItem(CLIPBOARD_KEY)
    }
  }, [])
  // #1316 review round 2: a spent cut is cleared only while the clipboard still holds it (`clearSpentCut`)
  const consumeCut = useCallback((cut: ClipboardData) => {
    if (clearSpentCut(cut)) setClipboardState(null)
  }, [])

  // Cross-tab clipboard sync
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CLIPBOARD_KEY) {
        setClipboardState(e.newValue ? JSON.parse(e.newValue) : null)
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Drag and drop state
  const [draggedIds, setDraggedIds] = useState<Set<string>>(new Set())
  const [dropTargetId, setDropTargetId] = useState<string | null>(null)
  const [forbiddenDropId, setForbiddenDropId] = useState<string | null>(null)

  const [linksExpanded, setLinksExpanded] = useState(true)
  // TWO strings, because the section is named in two grammatical roles and a
  // single one cannot serve both. This used to be one value ("Project Files"),
  // which was right while the header read like a noun phrase.
  //
  //  • `filesSectionHeader` — the header itself. "In this Project" answers the
  //    question the sidebar actually poses (where did this file come from:
  //    linked from a drive, or here), which "Project Files" did not.
  //  • `filesSectionName` — the section AS A PLACE: carried on the row as
  //    `data-file-name` (read back by `getItemMetaFromDOM`) and used as the
  //    paste-target fallback, which renders as "Paste into …". Feeding the
  //    header through there would produce "Paste into In this Project".
  //
  // They must still AGREE — the earlier bug was a header saying one thing while
  // the other two said "Workspace Files", so "Paste into …" named a section no
  // longer on screen.
  const filesSectionName = entityKind === 'miniapp' ? 'PDApp' : 'Project'
  const filesSectionHeader = `In this ${filesSectionName}`
  const [filesExpanded, setFilesExpanded] = useState(true)

  // Subscribe to the top-level folder's contents via TanStack Query. This is
  // the single source of fetches for the Files section — realtime
  // invalidations target this same cache key, so the query auto-refetches
  // without manual `loadWorkspaceFiles()` calls in event handlers.
  //
  // staleTime: 1000 collapses the "two handlers fire ~200ms apart" window
  // (e.g. a sibling LinkedFolderTree reacting to the same event). Each
  // mutation invalidates again, so freshness is per-event.
  const filesListOpts = useMemo<ListQueryOptions>(
    () => ({ sortBy: 'name', sortDir: 'asc', limit: 500 }),
    [],
  )
  const filesQuery = useFilesList(folderId, filesListOpts, {
    enabled: !!folderId,
    staleTime: 1000,
  })
  const loading = !!folderId && filesQuery.isLoading

  // Mirror the cached list result into the tree's working state. The merge
  // keeps any already-expanded grandchildren mounted on refetch (prevents
  // unmount-flicker when a realtime event invalidates the parent list).
  useEffect(() => {
    if (!folderId) {
      setWorkspaceFiles([])
      return
    }
    if (!filesQuery.data) return
    const newItems: FileNode[] = sortFileItems(
      filesQuery.data.files
        .filter((f: FileListItem) => f.name !== '.workspace.json')
        .map((f: FileListItem) => ({
          id: f.id,
          name: f.name,
          isFolder: f.isFolder,
          size: f.size,
          parentId: f.parentId ?? folderId,
          children: f.isFolder ? [] : undefined,
          isLoaded: false,
          isLoading: false,
          metadataJson: f.metadataJson,
          // Pointer kind, approval state and share health — one place for all three
          // mappings, because hand-copying them is what put the same defect in three
          // review rounds running. See `listingRow.ts`.
          ...pointerFactsFromListing(f),
          ...(f.effectivePermission ? { effectivePermission: f.effectivePermission } : {}),
        })),
    )
    setWorkspaceFiles(prev => mergeChildren(newItems, prev))
  }, [filesQuery.data, folderId])

  // Imperative refresh trigger — invalidates the cache key, which wakes
  // useFilesList to refetch. Kept as a callable to preserve the existing API
  // shape consumed by realtime/upload/permission-change handlers below.
  const loadWorkspaceFiles = useCallback(() => {
    if (!folderId) return
    queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', folderId] })
  }, [folderId, queryClient])

  // The person's Delete (#781) — see handleDelete. Path/kind of each item, for
  // closing its tabs; a job resumed after a reload has none recorded, and is
  // always a folder (only folders are queued).
  const deleteInfoRef = useRef(new Map<string, { isFolder: boolean; path?: string }>())
  const { deleteItems } = useDeleteRunner({
    onDeleted: (target, outcome, at) => {
      // #801: a split binned only the removable branches — the ROOT is still
      // there, so it must not be dropped from the tree (or its tabs closed); a
      // split through a mount binned SOURCE-tenant ids, which undo here can't use.
      if (at && !outcome.deletedInSource && !(outcome.binId === undefined && isMountId(target.id))) {
        undo.recordDeleteResult(at, target.id, target.name, outcome)
      }
      const info = deleteInfoRef.current.get(target.id)
      deleteInfoRef.current.delete(target.id)
      if (outcome.binId === undefined) {
        for (const id of outcome.binnedFileIds) onFileDeleted?.(id, { isFolder: false })
      } else {
        onFileDeleted?.(target.id, { isFolder: info?.isFolder ?? true, path: info?.path })
      }
    },
    // On every outcome: the listing is what tells the person what happened.
    onSettled: () => loadWorkspaceFiles(),
  })

  // Re-fetch when permissions change (role change, email switch)
  const prevCanWriteRef = useRef(canWrite)
  useEffect(() => {
    if (prevCanWriteRef.current !== canWrite) {
      prevCanWriteRef.current = canWrite
      loadWorkspaceFiles()
      setLinkedRefreshTrigger(n => n + 1)
    }
  }, [canWrite, loadWorkspaceFiles])

  // Re-fetch the linked trees when folder access changes (hidden ↔ visible). The ROOT listing is an
  // observed query: `FileListCacheSync` re-reads it on the same change (#1259 — reloading it here too
  // fetched it again).
  const prevAccessKeyRef = useRef(accessRefreshKey)
  useEffect(() => {
    if (accessRefreshKey !== undefined && prevAccessKeyRef.current !== accessRefreshKey) {
      prevAccessKeyRef.current = accessRefreshKey
      setLinkedRefreshTrigger(n => n + 1)
    }
  }, [accessRefreshKey])

  // ─── Reveal file (auto-expand + scroll into view) ─────────────────
  const [autoExpandIds, setAutoExpandIds] = useState<Set<string> | null>(null)
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null)
  // The active file whose reveal has actually COMPLETED (row selected). #674:
  // set ONLY when setSelection fires — never optimistically when a reveal is
  // merely scheduled — so a reveal that hasn't landed yet stays retryable.
  const revealedFileId = useRef<string | null>(null)

  const revealFile = useCallback(async (fileId: string): Promise<boolean> => {
    // Returns true if the target is IN-CONTEXT (under the workspace root or a
    // linked folder) and was revealed; false if it's out-of-context (in no
    // known tree) — the caller (e.g. openFile of an out-of-context folder)
    // then falls back to a drive view.
    // Revealing the workspace folder itself targets the root files section
    // section header (only data-file-id row in that section); also expand
    // the section so its children are visible under the highlighted root.
    if (fileId === folderId) {
      setFilesExpanded(true)
    }
    // Skip if already visible in DOM
    const existing = containerRef.current?.querySelector(`[data-file-id="${fileId}"]`) as HTMLElement | null
    if (existing) {
      // Cancel any poll still pending for a PREVIOUS target (#674 review): this
      // reveal resolves synchronously here, so a stale poll left running (now up
      // to 10s) could otherwise fire later and steal the selection to its old,
      // superseded target. Setting null re-runs the poll effect's cleanup.
      setScrollTargetId(null)
      existing.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      const meta = getItemMetaFromDOM(fileId)
      if (meta) setSelection(new Map([[fileId, meta]]))
      // Selection actually landed (warm path) → record the reveal as complete.
      revealedFileId.current = fileId
      return true
    }

    // Get ancestor chain via shared cache — useFile / getFileInfoCached has
    // probably already populated breadcrumbs for this fileId; fall back to a
    // direct fetch if not.
    try {
      const crumbs = await queryClient.fetchQuery({
        queryKey: fileKeys.breadcrumbs(fileId),
        queryFn: () => filesApi.getBreadcrumbs(fileId),
        staleTime: 30_000,
      })
      const crumbIds = crumbs.map(c => c.id)

      // Determine if file is in workspace files or a linked folder
      // Root folder IDs (papan_${tenantId}) may or may not appear in breadcrumbs
      const linkMatch = links.find(l =>
        crumbIds.includes(l.targetFolderId) || isRootFolderId(l.targetFolderId)
      )

      if (linkMatch) {
        setLinksExpanded(true)
        // Folders to expand: linked root + all crumbs after it
        const isRoot = isRootFolderId(linkMatch.targetFolderId)
        const rootIdx = isRoot ? 0 : crumbIds.indexOf(linkMatch.targetFolderId)
        // For root links, expand all ancestor folders; for others, start from the link target
        const foldersToExpand = new Set([linkMatch.targetFolderId, ...crumbIds.slice(rootIdx)])
        setAutoExpandIds(foldersToExpand)
      } else if (folderId && crumbIds.includes(folderId)) {
        setFilesExpanded(true)
        const rootIdx = crumbIds.indexOf(folderId)
        const foldersToExpand = new Set(crumbIds.slice(rootIdx + 1))
        setAutoExpandIds(foldersToExpand.size > 0 ? foldersToExpand : null)
      } else {
        // out-of-context: file is in no known tree (caller falls back to a drive
        // view). Cancel any poll pending for a previous target (#674 review) so
        // it can't fire later against a now-superseded reveal.
        // ACCEPTED EDGE: on the dominant auto-reveal path this file IS the active
        // one, so cancelling is exactly right. If an EXTERNAL caller reveals a
        // different out-of-context target while the active file's cold-load poll
        // is still pending (e.g. WorkspaceView opening a folder in a new tab),
        // this also cancels that pending poll — the only effect is a missed
        // auto-select of the active file's row, which self-heals on the next tab
        // switch. Not worth threading activeFileId into this callback to avoid.
        setScrollTargetId(null)
        return false
      }

      setScrollTargetId(fileId)
      return true
    } catch {
      // getBreadcrumbs failed — file may not exist. Cancel any stale pending poll.
      setScrollTargetId(null)
      return false
    }
  }, [folderId, links])

  // Scroll-to-target polling effect (expansion + child loading is async).
  // #674: the ancestor-expand cascade renders the target row progressively as
  // each level's children lazily load, so on a COLD load it can appear well
  // after the old fixed ~1s budget. waitForElement polls until the row exists or
  // a generous deadline — and we record the reveal (revealedFileId) ONLY when
  // selection actually fires. On deadline it stays un-recorded, so a later
  // activeFileId change (or the effect re-firing as links/folderId load) retries
  // instead of being permanently short-circuited by an optimistic "done" flag.
  useEffect(() => {
    if (!scrollTargetId) return
    const cancel = { cancelled: false }
    const targetId = scrollTargetId
    void waitForElement(
      () => containerRef.current?.querySelector(`[data-file-id="${targetId}"]`) as HTMLElement | null,
      { intervalMs: 50, deadlineMs: 10_000, cancel },
    ).then((el) => {
      if (cancel.cancelled) return
      if (el) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        const meta = getItemMetaFromDOM(targetId)
        if (meta) setSelection(new Map([[targetId, meta]]))
        // Only NOW is the reveal actually complete.
        revealedFileId.current = targetId
      }
      // Clear the pending target either way; on deadline we intentionally leave
      // revealedFileId unset so the reveal can be retried.
      setScrollTargetId(null)
      setAutoExpandIds(null)
    })
    return () => { cancel.cancelled = true }
  }, [scrollTargetId])

  // Auto-reveal the active file — on tab switch AND once its tree context
  // finishes loading. The first reveal on mount/reload can miss when `links`
  // (linked folders) hasn't populated yet, or when the tree/child fetches are
  // still in flight: revealFile then either returns false (out-of-context, not
  // yet linked) or schedules the async poll above. revealFile's identity changes
  // when links/folderId load, re-running this effect; because revealedFileId is
  // now set ONLY once the row is actually selected (#674), a still-pending reveal
  // is retried on those changes rather than being locked out by an optimistic
  // flag. Once selected it stops, so it won't yank the tree back if the user has
  // scrolled away.
  useEffect(() => {
    if (!activeFileId) { revealedFileId.current = null; return }
    if (activeFileId === revealedFileId.current) return
    void revealFile(activeFileId)
  }, [activeFileId, revealFile])

  // Folder loads, coordinated (#1058 code review): only a folder's latest load
  // settles it, an older call resolves once that one has settled (so `await
  // refreshFolder(...)` means "the folder has settled"), and a load asked
  // before a resync settles nothing and asks again — `useLatestFolderLoads`.
  const folderLoader = useCallback<FolderLoad>(async (childFolderId, settle, { fresh, reask }) => {
    // A resync's re-ask of a folder no longer in the tree (deleted meanwhile,
    // another workspace shown) asks nothing.
    if (reask && !findNode(workspaceFilesRef.current, childFolderId)) { await settle(() => {}, false); return }
    setWorkspaceFiles(prev => updateNode(prev, childFolderId, { isLoading: true }))
    // Only the FETCH is guarded: a problem settling (a superseded or re-asked
    // load) is not this folder failing to load.
    let newChildren: FileNode[]
    try {
      // TODO: limit: 500 is a workaround — tree should load all children.
      // Need virtual scrolling or server-side search for folders with 500+ files.
      const opts = { sortBy: 'name' as const, sortDir: 'asc' as const, limit: 500 }
      // Through `fetchAndCacheList`: one in-flight read per folder shared with the other trees, and
      // never an answer that predates an access change (#1260) or is for another identity (#1210).
      const res = await readTreeFolder(queryClient, childFolderId, opts, fresh ? 0 : 1000)
      newChildren = res.files.map((f: FileListItem) => ({
        id: f.id,
        name: f.name,
        isFolder: f.isFolder,
        size: f.size,
        parentId: f.parentId ?? childFolderId,
        children: f.isFolder ? [] : undefined,
        isLoaded: false,
        isLoading: false,
        metadataJson: f.metadataJson,
        ...pointerFactsFromListing(f),
        ...(f.effectivePermission ? { effectivePermission: f.effectivePermission } : {}),
      }))
    } catch (err) {
      // The identity changed again during the re-ask (#1210): not a failure — settle nothing; the
      // folder stays loadable and its next load reads as the identity then active.
      // So is a session ended under it (#1316 × #1210 — an identity switch ends the session first).
      if (err instanceof IdentityChangedError || isSessionEndedError(err)) { await settle(() => setWorkspaceFiles(prev => updateNode(prev, childFolderId, { isLoading: false })), false); return }
      console.error('[WorkspaceExplorer] Failed to load:', childFolderId, err)
      // Still not loaded: a click, a reveal or a resync retries; the row's
      // automatic loads skip it (`FileNode.loadFailed`), never a loop. A failed
      // REFRESH of a loaded folder keeps its rows, unflagged, as before.
      await settle(() => setWorkspaceFiles(prev => updateNode(prev, childFolderId, findNode(prev, childFolderId)?.isLoaded
        ? { isLoading: false }
        : { isLoading: false, loadFailed: true })), false)
      return
    }
    await settle(() => setWorkspaceFiles(prev => {
      const existing = findNode(prev, childFolderId)
      // Merge: preserve expanded grandchildren while marking them stale
      const merged = existing?.children?.length
        ? mergeChildren(newChildren, existing.children)
        : newChildren
      return updateNode(prev, childFolderId, { children: merged, isLoaded: true, isLoading: false, loadFailed: false })
    }))
  }, [queryClient])
  const { load: handleLoadChildren, resync: resyncFolderLoads } = useLatestFolderLoads(folderLoader)

  /** Refresh a specific folder — root or subfolder */
  // Bump to force linked folder trees to reload
  const [linkedRefreshTrigger, setLinkedRefreshTrigger] = useState(0)
  const [linkedDeepRefreshTrigger, setLinkedDeepRefreshTrigger] = useState(0)

  const workspaceFilesRef = useRef(workspaceFiles)
  workspaceFilesRef.current = workspaceFiles

  const refreshFolder = useCallback(async (parentId: string | null) => {
    if (parentId === folderId || parentId === null) {
      await loadWorkspaceFiles()
    } else if (findNode(workspaceFilesRef.current, parentId)) {
      await handleLoadChildren(parentId)
    } else {
      // parentId is inside a linked folder tree — bump trigger to reload them
      setLinkedRefreshTrigger(n => n + 1)
    }
  }, [folderId, loadWorkspaceFiles, handleLoadChildren])

  // Auto-refresh workspace files on realtime file events (debounced).
  //
  // Old behavior (pre-Phase 3) called loadWorkspaceFiles() on every event,
  // which marked every expanded subfolder stale and triggered a cascade of
  // re-fetches per move (production observed: ~12 list refetches per move).
  //
  // New behavior collects the parentId / oldParentId carried by each event
  // during the debounce window and refreshes ONLY those folders. Events
  // missing both fields (older publishers, version restore, etc.) fall back
  // to a broad refresh so we don't miss anything.
  const { onFileEvent } = useRealtime()
  const wsRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingParentsRef = useRef<Set<string | null>>(new Set())
  const pendingBroadRef = useRef(false)
  useEffect(() => {
    return onFileEvent((event) => {
      const ev = event as typeof event & { parentId?: string | null; oldParentId?: string | null }
      const hasParent = ev.parentId !== undefined
      const hasOldParent = ev.oldParentId !== undefined
      if (!hasParent && !hasOldParent) {
        pendingBroadRef.current = true
      } else {
        if (hasParent) pendingParentsRef.current.add(ev.parentId ?? null)
        if (hasOldParent) pendingParentsRef.current.add(ev.oldParentId ?? null)
      }

      if (wsRefreshTimerRef.current) clearTimeout(wsRefreshTimerRef.current)
      wsRefreshTimerRef.current = setTimeout(() => {
        wsRefreshTimerRef.current = null
        const parents = pendingParentsRef.current
        const broad = pendingBroadRef.current
        pendingParentsRef.current = new Set()
        pendingBroadRef.current = false

        // Broad fallback: reload workspace root + signal linked trees to reload.
        // (LinkedFolderTree's own realtime handler also covers broad events,
        // but bumping the trigger is safe and gives a single deterministic refresh.)
        if (broad) {
          loadWorkspaceFiles()
          setLinkedRefreshTrigger(n => n + 1)
          return
        }

        // Surgical: only refresh parents that live in the workspace tree.
        // Parents in linked trees are intentionally skipped — each
        // LinkedFolderTree subscribes to the same events and refreshes only
        // the subfolder that actually changed, so we don't want to bump
        // linkedRefreshTrigger and force a wholesale reload of every tree.
        //
        // Invalidate first, then fetch. With staleTime: 1000 in the imperative
        // fetch path, this lets sibling subscribers (the matching LinkedFolderTree
        // for the same parent, or the WorkspaceExplorer itself) hit the cache
        // when they fire ~ms later. Each new mutation invalidates again, so
        // freshness is guaranteed per-event.
        for (const pid of parents) {
          queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', pid] })
          if (pid === folderId || pid === null) {
            loadWorkspaceFiles()
          } else if (findNode(workspaceFilesRef.current, pid)) {
            handleLoadChildren(pid)
          }
          // else: parent lives inside a linked folder tree; let it self-refresh
        }
      }, 500)
    })
  }, [onFileEvent, loadWorkspaceFiles, folderId, handleLoadChildren, queryClient])

  // #1264 — an action on a row of the workspace tree was refused (it may be gone, its delete never
  // announced): re-read the folder that holds it. Rows of a linked tree are its own (it subscribes too).
  useEffect(() => onListedRowRefused((fileId) => {
    const node = findNode(workspaceFilesRef.current, fileId)
    if (!node) return
    const parent = node.parentId
    void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', parent] })
    if (parent === folderId || parent === null || !findNode(workspaceFilesRef.current, parent)) loadWorkspaceFiles()
    else handleLoadChildren(parent)
  }), [folderId, loadWorkspaceFiles, handleLoadChildren, queryClient])

  // #1058: a resync — events were missed while the socket was down — can mean
  // any loaded folder is out of date, and no event will say which. Reload the
  // root and mark every loaded folder stale, so each expanded one reloads. The
  // list cache is marked stale here too rather than trusting that
  // `FileListCacheSync`'s handler for the same signal ran first — else a reload
  // inside the 1 s `staleTime` would be served from the old cache. Each linked
  // tree does the same on its own.
  const { onResync } = useRealtime()
  useEffect(() => {
    return onResync((kind) => {
      if (kind !== 'file') return
      // Loads in flight were asked before the reconnect: each settles nothing
      // and asks again (`LatestFolderLoads`). Every loaded folder stale, and no
      // longer loading, so each expanded one asks too. An ask made while a
      // pre-reconnect read is STILL in flight shares that read (`fetchQuery`
      // de-duplicates by key), which asks again inside its own fetch — the
      // resync is an access-change boundary (`markResyncBoundary`, #1210).
      resyncFolderLoads()
      markListCacheStale(queryClient)
      setWorkspaceFiles(prev => markTreeStale(prev))
      loadWorkspaceFiles()
    })
  }, [onResync, loadWorkspaceFiles, queryClient, resyncFolderLoads])

  // Owner, 2026-10-03: a PERMISSION change can flip what this viewer may write here without any
  // file changing — e.g. an app's grants change and they no longer cover its reach, so everything
  // inside the PDApp turns read-only (or back). Re-read the root and every folder already loaded,
  // so the read-only locks follow live. At most one refresh per interval (`createAccessBurst`: a
  // change after a quiet spell refreshes within 300 ms). The burst outlives the subscription's re-runs, so a
  // callback changing mid-burst doesn't drop the refresh; it runs the latest one.
  const { onPermissionChanged } = useRealtime()
  const permRefreshRef = useRef<() => void>(() => {})
  // Set in a layout effect, never during render (as `useLatestFolderLoads`): a render React
  // discards never becomes the refresh.
  useLayoutEffect(() => { permRefreshRef.current = () => {
    // The per-file permission cache (every OPEN editor tab's read-only state, I116) and the ROOT
    // listing (an observed query) are re-read app-wide on the same signal — `FileListCacheSync`,
    // one batched request (#1259); reloading the root here too fetched it two or three times. The
    // loaded subfolders are read imperatively (no observer), so they are re-read here.
    for (const id of loadedFolderIds(workspaceFilesRef.current)) {
      queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', id] })
      handleLoadChildren(id)
    }
    setLinkedDeepRefreshTrigger(n => n + 1)
  } })
  // #1259: a hidden (cached) workspace re-reads once when shown, and a change naming another
  // member is skipped (`changesViewerAccess`).
  const whenShown = useWhenShown()
  const [permBurst] = useState(() => createAccessBurst(() => whenShown(() => permRefreshRef.current())))
  useEffect(() => onPermissionChanged((event) => { if (changesViewerAccess(event)) permBurst.push() }), [onPermissionChanged, permBurst])
  // Closed with a refresh pending loses nothing: `FileListCacheSync` marked the permission and
  // list caches stale the moment the change arrived (#1260).
  useEffect(() => () => { permBurst.cancel() }, [permBurst])

  const upload = useUploadService({ onComplete: (fid) => { if (fid) refreshFolder(fid) } })

  const handleFolderSelected = async (selectedFolderId: string) => {
    // Block linking the workspace's own folder or its subfolders
    if (folderId && (selectedFolderId === folderId || hasNode(workspaceFiles, selectedFolderId))) {
      toast.error('Cannot link a folder that is already inside the workspace')
      return
    }
    // Ensure workspace exists so auto-save can persist the link
    if (!folderId) {
      const created = await onEnsureWorkspace?.()
      if (!created) return
    }
    onAddLink(selectedFolderId)
    setShowFolderPicker(false)
  }

  // Context menu for workspace file/folder items (scope: null)
  const handleItemContextMenu = useCallback((item: FileNode, e: React.MouseEvent) => {
    e.preventDefault()
    const meta: SelectionEntry = { name: item.name, isFolder: item.isFolder, parentId: item.parentId, parentName: null, scope: null }
    // Effective selection the menu acts on: the existing multi-selection if the
    // right-clicked item is part of it, else just this item.
    const effEntries: Array<[string, SelectionEntry]> = selection.has(item.id) ? [...selection.entries()] : [[item.id, meta]]
    if (!selection.has(item.id)) setSelection(new Map([[item.id, meta]]))
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      targetFolderId: item.isFolder ? item.id : null,
      itemId: item.id,
      itemName: item.name,
      itemIsFolder: item.isFolder,
      itemParentId: item.parentId ?? null,
      itemPointer: item,
      itemCode: item,
      itemReshareBlocked: item.reshareBlocked,
      itemShareBroken: item.shareBroken,
      scope: null,
      selEntries: effEntries,
    })
  }, [selection])

  // The empty space below the tree belongs to the files section — that is where
  // a new file would land, and where a paste would go.
  const handleBackgroundContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, targetFolderId: null, section: 'files' })
  }, [])

  // Section headers. `stopPropagation` because the whole explorer carries the
  // background handler — without it a header right-click bubbles up and every
  // header shows the files-section menu, which is how the "Linked from Drives"
  // header came to offer Upload and Paste.
  const handleSectionContextMenu = useCallback((section: 'files' | 'links') => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setSelection(new Map())
    setContextMenu({ x: e.clientX, y: e.clientY, targetFolderId: null, section })
  }, [])

  const handleLinkedFolderContextMenu = useCallback((linkId: string, targetFolderId: string, folderName: string, e: React.MouseEvent, rootShareFacts: RootShareFacts) => {
    e.preventDefault()
    handleSelect(targetFolderId, linkId, folderName, true)
    // The facts come straight off the LINK — the server sends them with the listing
    // this section is built from. They used to be read from the listing that loads
    // the root's children, which is lazy, so "Share link…" appeared on a linked root
    // only after you had expanded it once and the gate had to refuse before that
    // rather than guess. `linkedRootShareRow` translates them into the gate's fields
    // in one place, so a test can exercise the decision instead of re-deriving it.
    setContextMenu({ x: e.clientX, y: e.clientY, targetFolderId, linkId, itemId: targetFolderId, itemName: folderName, itemIsFolder: true, itemParentId: null,
      ...linkedRootShareRow(rootShareFacts),
      scope: linkId })
  }, [handleSelect])

  // Context menu for items inside linked folders (rename support)
  const handleLinkedItemContextMenu = useCallback((item: ContextMenuRow, e: React.MouseEvent, linkId: string) => {
    e.preventDefault()
    const meta: SelectionEntry = { name: item.name, isFolder: item.isFolder, parentId: item.parentId ?? null, parentName: item.parentName ?? null, scope: linkId }
    const effEntries: Array<[string, SelectionEntry]> = selection.has(item.id) ? [...selection.entries()] : [[item.id, meta]]
    // If right-clicked item not in selection, replace selection
    if (!selection.has(item.id)) {
      setSelection(new Map([[item.id, meta]]))
      lastSelectedId.current = item.id
    }
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      targetFolderId: item.isFolder ? item.id : null,
      itemId: item.id,
      itemName: item.name,
      itemIsFolder: item.isFolder,
      itemParentId: item.parentId ?? null,
      itemParentName: item.parentName ?? null,
      itemPointer: item,
      itemCode: item,
      itemReshareBlocked: item.reshareBlocked,
      itemShareBroken: item.shareBroken,
      scope: linkId,
      selEntries: effEntries,
    })
  }, [selection])

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) {
      setAiSubmenuOpen(false)
      return
    }
    const handler = (e: MouseEvent) => {
      // The AI Access submenu flyout is portaled to <body>, outside the main
      // menu (contextMenuRef) — treat a click on it as inside the menu.
      if ((e.target as Element)?.closest?.('[data-file-context-submenu]')) return
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null)
        setAiSubmenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler, true)
    return () => document.removeEventListener('mousedown', handler, true)
  }, [contextMenu])

  // Clamp context menu position to stay within viewport
  useLayoutEffect(() => {
    if (!contextMenu || !contextMenuRef.current) return
    const el = contextMenuRef.current
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    let { x, y } = contextMenu
    if (x + rect.width > vw) x = Math.max(0, vw - rect.width - 4)
    if (y + rect.height > vh) y = Math.max(0, vh - rect.height - 4)
    if (x !== contextMenu.x) el.style.left = `${x}px`
    if (y !== contextMenu.y) el.style.top = `${y}px`
  }, [contextMenu])

  // Clear selection when clicking outside the explorer (but not the menu bar)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        // Don't clear when clicking the workspace menu bar — it operates on the selection
        if ((e.target as Element)?.closest?.('[data-workspace-menubar]')) return
        // Don't clear when clicking the (portaled) context menu — its actions
        // read the selection, and the menu lives under <body>, outside
        // containerRef. Without this, mousedown on a menu item wipes the
        // selection before the item's click fires and Delete/Copy/Cut/Download
        // silently no-op.
        if (contextMenuRef.current?.contains(e.target as Node)) return
        // Same reason as above: the portaled AI Access submenu flyout is not
        // under contextMenuRef, but its options act on the selection.
        if ((e.target as Element)?.closest?.('[data-file-context-submenu]')) return
        setSelection(new Map())
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Resolve the effective parent for create operations
  const getEffectiveParent = useCallback(() => {
    // From context menu (right-click on a folder)
    if (contextMenu?.targetFolderId) return contextMenu.targetFolderId
    // From selection (single selected folder → create inside it; file → its parent)
    if (selection.size === 1) {
      const [id, meta] = [...selection.entries()][0]
      if (meta.isFolder) return id
      if (meta.parentId) return meta.parentId
    }
    return folderId
  }, [contextMenu, folderId, selection])

  // Like getEffectiveParent but auto-creates workspace if needed
  const ensureParent = useCallback(async (): Promise<string | null> => {
    const parentId = getEffectiveParent()
    if (parentId) return parentId
    return onEnsureWorkspace?.() ?? null
  }, [getEffectiveParent, onEnsureWorkspace])

  // Run an async operation that may call ensureParent/ensureWorkspace.
  // Signals busy to parent so it won't navigate away mid-operation.
  const withBusyGuard = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    onBusyChange?.(true)
    try {
      return await fn()
    } finally {
      onBusyChange?.(false)
    }
  }, [onBusyChange])

  const handleUnlink = useCallback(() => {
    if (contextMenu?.linkId) {
      onRemoveLink(contextMenu.linkId)
    }
    setContextMenu(null)
  }, [contextMenu, onRemoveLink])

  const handleIncludeFolder = useCallback(() => {
    setContextMenu(null)
    setShowFolderPicker(true)
  }, [])

  // Rename (scoped to a specific linked tree or workspace files)
  const renamingOldNameRef = useRef<string | null>(null)

  const startRename = useCallback((id: string, scope?: string | null, oldName?: string) => {
    setContextMenu(null)
    // Resolve the old name from the explicit arg, the workspace-files tree, OR
    // the current selection. The selection fallback is load-bearing for LINKED
    // folders / linked-tree items: they live in the per-link trees, NOT in
    // `workspaceFiles`, so findNode() returns null for them. Without it the
    // commit sees oldName=null and skips filesApi.moveFile (which only fires
    // when `oldName && oldName !== newName`), so the rename silently reverts.
    renamingOldNameRef.current = oldName || findNode(workspaceFiles, id)?.name || selection.get(id)?.name || null
    setRenamingId(id)
    setRenamingScope(scope ?? null)
  }, [workspaceFiles, selection])

  const handleCreateFolder = useCallback(async () => {
    setContextMenu(null)
    const parentId = await ensureParent()
    if (!parentId) return
    const defaultName = `New Folder ${folderCounterRef.current++}`
    try {
      const result = await filesApi.createFolder(parentId, defaultName)
      if (parentId !== folderId) setAutoExpandIds(new Set([parentId]))
      await refreshFolder(parentId)
      // Track as pending and enter rename mode
      pendingNewItemRef.current = { id: result.folder.id, isFolder: true }
      startRename(result.folder.id, null, defaultName)
    } catch (err) {
      console.error('[WorkspaceExplorer] Failed to create folder:', err)
    }
  }, [ensureParent, refreshFolder, folderId, startRename])

  // Create new text file with inline rename (context menu)
  const handleNewFile = useCallback(async () => {
    setContextMenu(null)
    const parentId = await ensureParent()
    if (!parentId) return
    const defaultName = `Untitled-${untitledCounterRef.current++}`
    try {
      const result = await filesApi.uploadFile(parentId, defaultName, new Blob([]))
      if (parentId !== folderId) setAutoExpandIds(new Set([parentId]))
      await refreshFolder(parentId)
      // Track as pending and enter rename mode
      pendingNewItemRef.current = { id: result.fileId, isFolder: false }
      startRename(result.fileId, null, defaultName)
    } catch (err) {
      console.error('[WorkspaceExplorer] Failed to create file:', err)
    }
  }, [ensureParent, refreshFolder, folderId, startRename])

  // Create new text file and open immediately (File menu)
  const handleNewFileQuick = useCallback(async () => {
    const parentId = await ensureParent()
    if (!parentId) return
    const defaultName = `Untitled-${untitledCounterRef.current++}`
    try {
      const result = await filesApi.uploadFile(parentId, defaultName, new Blob([]))
      if (parentId !== folderId) setAutoExpandIds(new Set([parentId]))
      await refreshFolder(parentId)
      onFileOpen(result.fileId)
    } catch (err) {
      console.error('[WorkspaceExplorer] Failed to create file:', err)
    }
  }, [ensureParent, refreshFolder, folderId, onFileOpen])

  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0) return
    const needsCreate = !uploadTargetRef.current && !folderId
    const doUpload = async () => {
      const parentId = uploadTargetRef.current || await ensureParent()
      if (!parentId) return
      await upload.handlePickedFiles(fileList, parentId, resolveConflict)
      e.target.value = ''
    }
    if (needsCreate) {
      await withBusyGuard(doUpload)
    } else {
      await doUpload()
    }
  }, [ensureParent, folderId, resolveConflict, withBusyGuard, upload])

  // ─── Delete handler ─────────────────────────────────────────────────

  const handleDelete = useCallback(async (entries?: Array<[string, SelectionEntry]>) => {
    const src = entries ?? [...selection.entries()]
    if (src.length === 0) return

    // Resolve metadata for all selected items
    const items: { id: string; name: string; isFolder: boolean; path?: string }[] = []
    for (const [id, meta] of src) {
      const isFolder = meta.isFolder
      if (meta.name) {
        items.push({ id, name: meta.name, isFolder })
      } else {
        try {
          const info = await fetchAndCacheFileInfo(queryClient, id)
          items.push({ id, name: info.name, isFolder })
        } catch { /* skip */ }
      }
    }
    if (items.length === 0) return

    // Pre-fetch paths for folders (needed to close child tabs after deletion)
    for (const item of items) {
      if (item.isFolder) {
        try {
          const info = await filesApi.getFileInfo(item.id)
          item.path = info.path
        } catch { /* proceed without path */ }
      }
    }

    const message = items.length === 1
      ? `Delete "${items[0].name}"? It will be moved to the bin.`
      : `Delete ${items.length} items? They will be moved to the bin.`

    const confirmed = await confirm({
      title: 'Delete',
      message,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return

    // The drive's runner (#781): one toast for the run, big folders as
    // followed background jobs. Undo, tab closing and the reload ride its
    // `onDeleted`/`onSettled` (set up beside `loadWorkspaceFiles`).
    for (const item of items) deleteInfoRef.current.set(item.id, { isFolder: item.isFolder, path: item.path })
    await deleteItems(items.map((i) => ({ id: i.id, name: i.name })))
    setSelection(new Map())
  }, [selection, confirm, deleteItems, queryClient])

  // ─── Copy / Cut / Paste handlers ───────────────────────────────────

  const resolveSelectionItems = useCallback(async (entries: Iterable<[string, SelectionEntry]> = selection): Promise<{ id: string; name: string; isFolder: boolean; parentId: string | null }[]> => {
    const items: { id: string; name: string; isFolder: boolean; parentId: string | null }[] = []
    for (const [id, meta] of entries) {
      if (meta.name) {
        items.push({ id, name: meta.name, isFolder: meta.isFolder, parentId: meta.parentId })
      } else {
        try {
          const info = await fetchAndCacheFileInfo(queryClient, id)
          items.push({ id, name: info.name, isFolder: info.isFolder, parentId: info.parentId })
        } catch { /* skip */ }
      }
    }
    return items
  }, [selection, queryClient])

  const handleCopy = useCallback(async (entries?: Array<[string, SelectionEntry]>) => {
    const src = entries ?? [...selection.entries()]
    if (src.length === 0) return
    const items = await resolveSelectionItems(src)
    if (items.length === 0) return
    setClipboard(stampClipboard(items, false))
    toast.success(items.length === 1 ? `Copied "${items[0].name}"` : `Copied ${items.length} items`)
  }, [selection, resolveSelectionItems, setClipboard, toast])

  const handleCut = useCallback(async (entries?: Array<[string, SelectionEntry]>) => {
    const src = entries ?? [...selection.entries()]
    if (src.length === 0) return
    const items = await resolveSelectionItems(src)
    if (items.length === 0) return
    setClipboard(stampClipboard(items, true))
    toast.success(items.length === 1 ? `Cut "${items[0].name}"` : `Cut ${items.length} items`)
  }, [selection, resolveSelectionItems, setClipboard, toast])

  const handlePaste = useCallback(async () => {
    if (!clipboard) return
    const { items, isCut } = clipboard
    // (#752) Items copied in ANOTHER tenant. The explorer only pastes within
    // one tenant; the cross-tenant copy (with its confirmation) lives in the
    // drive — say so instead of sending foreign ids to a same-tenant copy.
    const activeTenant = getActiveTenantId()
    if (clipboard.tenantId && activeTenant && clipboard.tenantId !== activeTenant) {
      toast.error(isCut
        ? t('driveView.toast.crossTenantMove', { defaultValue: 'Items can’t be moved from one of your drives to another. Copy them instead — moving into or out of a shared folder still works.' })
        : t('driveView.crossTenantPaste.useDrive'))
      return
    }

    // Target priority: context menu folder > first selected folder > first selected file's parent > workspace root
    // When contextMenu exists, its targetFolderId is authoritative (null = workspace root)
    let targetId = contextMenu?.targetFolderId ?? null
    if (!contextMenu && !targetId && selection.size > 0) {
      const firstEntry = [...selection.entries()][0]
      const [firstId, firstMeta] = firstEntry
      if (firstMeta.isFolder) {
        targetId = firstId
      } else {
        const node = findNode(workspaceFiles, firstId)
        if (node?.parentId) {
          targetId = node.parentId
        } else {
          try {
            const info = await fetchAndCacheFileInfo(queryClient, firstId)
            targetId = info.parentId
          } catch { /* fall through to workspace root */ }
        }
      }
    }
    if (!targetId) targetId = folderId
    if (!targetId) return

    // #1316 review round 2 (owner, 2026-10-28): the paste the person started FINISHES as the identity that started it —
    // every request is sent as it (`batch.opts()`), a tenant / identity switch mid-batch neither stops it nor sends the
    // rest as the next session; what it SHOWS (toasts, progress, an undo record, the clipboard) stops with its session.
    const batch = pinnedBatch()
    const at = captureUndoContext()   // D134: the batch's identity — every item's requests are sent as it
    const shown = () => !batch.ended()
    try {
      if (isCut) {
        let moved = 0
        let copiedOut = 0
        for (const item of items) {
          if (item.id === targetId) continue
          try {
            const r = await withMoveProgress(toast, t('driveView.toast.moving', { name: item.name }), (hooks) => filesApi.moveFile(item.id, { parentId: targetId }, { ...hooks, ...batch.opts() }), batch.ended)
            if (r?.chainEscape || r?.crossTenant || r?.accessChange) {
              // These guards resolve via the main-drive dialogs (ChainEscape /
              // Share / access-diff confirm) the workspace explorer doesn't host
              // — surface it instead of silently no-op'ing on the 409 body.
              if (shown()) toast.error(r?.error || `"${item.name}" can't be moved here — do it from the main drive.`)
              continue
            }
            if (r?.copied) {
              // Out of a received `.shared`: the server COPIED (source untouched).
              // Not a move — no move-undo; the new items are a copy-undo.
              const ids = r.copiedItems?.map((x) => x.id) ?? (r.file?.id ? [r.file.id] : [])
              if (ids.length > 0) undo.recordCopy(at, ids)
              if (r.copyErrors?.length && shown()) toast.error(`"${item.name}" was only partly copied: ${r.copyErrors[0].message}`)
              copiedOut++
              continue
            }
            // #801: a split records a split undo (not through a mount — its ids are the
            // source tenant's), a replay none; either says what happened.
            if (!(r.split && isMountId(item.id))) undo.recordMoveResult(at, item.id, item.parentId, item.name, r)
            const splitMsg = moveResultMessage(t, r)
            if (splitMsg) { if (shown()) toast.info(`${item.name}: ${splitMsg}`) }
            else moved++
          } catch (err) {
            if (shown()) toast.error(`Failed to move "${item.name}": ${moveErrorText(err, t)}`)
          }
        }
        // #1316 review round 2: the cut is spent whether or not the batch's session ended (it finished as its starter;
        // re-pasting would move the items again) — cleared while the clipboard still holds it
        consumeCut(clipboard)
        // the batch finished as its starter — nothing of it is shown in the session after its own
        if (!shown()) return
        // Only claim success for items that actually moved — a move blocked by a
        // 409 guard already toasted its own error above.
        if (moved > 0) toast.success(`Moved ${moved} item(s)`)
        if (copiedOut > 0) toast.success(`Copied ${copiedOut} item(s)`)
      } else {
        const pasted = await pasteItems(items, targetId, 'copy', resolveConflict, { batch })
        if (!shown()) return
        const { errors } = pasted
        // Files a Replace wrote a new version into count as pasted too (reported apart from `copied`).
        const landed = pasted.copied.length + (pasted.versioned?.length ?? 0)
        if (landed > 0) {
          // ONE undo step for the batch: deletes what it created, restores what its Replace overwrote.
          const rec = pasteUndoRecord(pasted)
          if (rec) undo.recordCopy(at, rec.copiedFileIds, rec.versioned)
          toast.success(pasteToastText(t, pasted.copied.map((x) => x.name), (pasted.versioned ?? []).map((x) => x.name)) ?? '')
        }
        if (errors.length > 0) {
          // Per-source failures from the server. Resolve filenames from
          // the clipboard items we just sent so the user can identify
          // which item failed (the response only carries sourceId).
          const itemById = new Map(items.map(i => [i.id, i.name]))
          const describe = (e: { sourceId: string; message: string }) =>
            `${itemById.get(e.sourceId) ?? e.sourceId}: ${e.message}`
          toast.error(errors.length > 1
            ? `${errors.length} items failed to copy — ${describe(errors[0])} (+${errors.length - 1} more)`
            : `Copy failed — ${describe(errors[0])}`)
        }
      }
      loadWorkspaceFiles()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      if (shown()) toast.error(`Paste failed: ${msg}`)
    }
    setContextMenu(null)
  }, [clipboard, contextMenu, selection, folderId, workspaceFiles, undo, toast, loadWorkspaceFiles, consumeCut, resolveConflict, queryClient, t])

  // Resolve the name of the folder that a paste would target
  const getPasteTargetName = useCallback((): string | null => {
    if (!clipboard) return null
    // Context menu target takes priority
    if (contextMenu) {
      if (contextMenu.targetFolderId) {
        const link = links.find(l => l.targetFolderId === contextMenu.targetFolderId)
        if (link) return link.alias || contextMenu.itemName || null
        const node = findNode(workspaceFiles, contextMenu.targetFolderId)
        if (node) return node.name
        return contextMenu.itemName || null
      }
      // File right-clicked — paste goes to its parent folder
      if (contextMenu.itemParentName) return contextMenu.itemParentName
      if (contextMenu.itemParentId) {
        const node = findNode(workspaceFiles, contextMenu.itemParentId)
        if (node) return node.name
      }
      return filesSectionName
    }
    // Selection-based: folder → paste inside it; file → paste in its parent
    if (selection.size > 0) {
      const [firstId, firstMeta] = [...selection.entries()][0]
      if (firstMeta.isFolder) {
        const link = links.find(l => l.targetFolderId === firstId)
        if (link) return link.alias || firstMeta.name
        return firstMeta.name
      }
      // File selected → show parent folder name
      if (firstMeta.parentName) return firstMeta.parentName
      if (firstMeta.parentId) {
        const node = findNode(workspaceFiles, firstMeta.parentId)
        if (node) return node.name
      }
    }
    return filesSectionName
  }, [clipboard, contextMenu, selection, links, workspaceFiles, filesSectionName])

  // Expose actions to parent via ref
  useImperativeHandle(ref, () => ({
    copy: handleCopy,
    cut: handleCut,
    paste: handlePaste,
    delete: handleDelete,
    getSelectedName: () => {
      if (selection.size === 0) return null
      if (selection.size === 1) return [...selection.values()][0].name
      return `${selection.size} items`
    },
    getSelectedCount: () => selection.size,
    getSelectedNames: () => [...selection.values()].map(e => e.name),
    getSelectedIds: () => [...selection.keys()],
    getSelectedItems: () => [...selection.entries()].map(([id, e]) => ({ id, isFolder: e.isFolder })),
    getPasteTargetName,
    getDeleteLabel: () => {
      if (selection.size === 0) return 'Delete'
      const linkRootIds = new Set(links.map(l => l.targetFolderId))
      const allAreLinkedRoots = [...selection.keys()].every(id => linkRootIds.has(id))
      return allAreLinkedRoots ? 'Unlink' : 'Delete'
    },
    rename: () => {
      if (selection.size === 1) {
        const [id, meta] = [...selection.entries()][0]
        startRename(id, meta.scope, meta.name)
      }
    },
    createFolder: handleCreateFolder,
    newFile: handleNewFile,
    newFileQuick: handleNewFileQuick,
    prepareUpload: () => { uploadTargetRef.current = getEffectiveParent() || folderId },
    linkFolder: handleIncludeFolder,
    revealFile,
    focus: () => containerRef.current?.focus(),
  }), [handleCopy, handleCut, handlePaste, handleDelete, handleCreateFolder, handleNewFile, handleNewFileQuick, getEffectiveParent, folderId, handleIncludeFolder, startRename, selection, links, getPasteTargetName, revealFile])

  // Register explorer commands for the command registry
  const clipboardForCmd = useMemo(() => {
    try {
      const raw = localStorage.getItem('papan-clipboard')
      const clip = raw ? JSON.parse(raw) as ClipboardData : null
      // (#752) Another tenant's clipboard isn't pasteable here — don't enable Paste for it.
      if (clip?.tenantId && clip.tenantId !== getActiveTenantId()) return null
      return clip
    } catch { return null }
  }, [selection]) // re-check when selection changes (triggers menu re-read)

  const explorerCommands = useMemo((): Command[] => {
    const count = selection.size
    const selName = count === 1 ? [...selection.values()][0]?.name : null
    const label = count > 1 ? `${count} items` : selName ? `"${selName.length > 20 ? selName.slice(0, 19) + '…' : selName}"` : null
    const linkRootIds = new Set(links.map(l => l.targetFolderId))
    const allLinkedRoots = count > 0 && [...selection.keys()].every(id => linkRootIds.has(id))
    const delLabel = allLinkedRoots ? 'Unlink' : 'Delete'

    return [
      { id: 'edit.copy', label: label ? `Copy ${label}` : 'Copy', icon: Copy as ComponentType<{size?: number}>, shortcut: { key: 'c', mod: true }, execute: handleCopy, enabled: count > 0 && !!canCopy, menuGroup: 'edit', menuOrder: 10 },
      { id: 'edit.cut', label: label ? `Cut ${label}` : 'Cut', icon: Scissors as ComponentType<{size?: number}>, shortcut: { key: 'x', mod: true }, execute: handleCut, enabled: count > 0 && !!canWrite, menuGroup: 'edit', menuOrder: 11 },
      { id: 'edit.paste', label: clipboardForCmd ? 'Paste' : 'Paste', icon: ClipboardPaste as ComponentType<{size?: number}>, shortcut: { key: 'v', mod: true }, execute: handlePaste, enabled: !!clipboardForCmd && !!canWrite, menuGroup: 'edit', menuOrder: 12, separator: 'after' },
      { id: 'edit.rename', label: selName ? `Rename "${selName.length > 20 ? selName.slice(0, 19) + '…' : selName}"` : 'Rename', icon: Pencil as ComponentType<{size?: number}>, shortcut: { key: 'F2' }, execute: () => { if (count === 1) { const [id, meta] = [...selection.entries()][0]; startRename(id, meta.scope, meta.name) } }, enabled: count === 1 && !!canWrite, menuGroup: 'edit', menuOrder: 40 },
      { id: 'edit.delete', label: label ? `${delLabel} ${label}` : delLabel, icon: Trash2 as ComponentType<{size?: number}>, shortcut: { key: 'Backspace' }, execute: handleDelete, enabled: count > 0 && !!canWrite, menuGroup: 'edit', menuOrder: 50, danger: true },
    ]
  }, [selection, links, clipboardForCmd, canWrite, canCopy, handleCopy, handleCut, handlePaste, handleDelete, startRename])

  useCommandScope('explorer', 'workspace', explorerCommands)
  const cmdRegistry = useCommandRegistryOptional()

  const handleRenameCommit = useCallback(async (id: string, newName: string, currentName?: string) => {
    // Prefer the CURRENT name passed straight from the tree row (always fresh)
    // over renamingOldNameRef, which is seeded from the selection and can go
    // STALE after a prior rename of the same still-selected item. Concretely:
    // rename `x.shared` → `x.share`, then (without re-selecting) back to
    // `x.shared` — the cached old name was still `x.shared`, so oldName ===
    // newName and the moveFile below was skipped, silently no-op'ing the rename.
    const oldName = currentName ?? renamingOldNameRef.current
    const pendingItem = pendingNewItemRef.current
    const wasNewFile = pendingItem?.id === id && !pendingItem.isFolder
    pendingNewItemRef.current = null  // accepted — no longer pending
    setRenamingId(null)
    setRenamingScope(null)
    renamingOldNameRef.current = null
    containerRef.current?.focus()

    // Warn before renaming a live `.shared` OFF its suffix — that detach silently
    // promotes a demoted "Conflict" copy to the live share (issue #157) and can change
    // access. Gate on the name; the server confirms it's a live share + that a copy
    // waits. Strings reuse the shared `driveView.rename.shareDetach*` i18n keys
    // (#157 re-review M3 — no longer hardcoded-English, unlike the extension warning below).
    if (oldName != null && isSharedPointer(oldName) && !isSharedPointer(newName)) {
      let willPromote = false
      let promoteName: string | undefined
      let path: string[] | undefined
      let driveName: string | undefined
      try {
        const preview = await filesApi.promotePreview(id)
        willPromote = !!preview.willPromote
        promoteName = preview.promote?.name?.replace(/\.shared$/i, '')
        path = preview.promote?.path
        driveName = preview.promote?.driveName ?? undefined
      } catch {
        willPromote = true // fail safe — warn rather than rename silently
      }
      if (willPromote) {
        const here = oldName.replace(/\.shared$/i, '')
        const proceed = await confirm({
          title: t('driveView.rename.shareDetachTitle'),
          message: (
            <>
              {promoteName
                ? t('driveView.rename.shareDetachMessage', { name: here, promoteName })
                : t('driveView.rename.shareDetachGeneric', { name: here })}
              {path && path.length > 0 && (
                <span className="mt-3 block rounded-md themed-bg-tertiary px-3 py-2">
                  <span className="mb-1 block text-xs themed-text-muted">{t('driveView.rename.shareDetachWillActivate')}</span>
                  <span className="block break-all font-mono text-xs themed-text">{[tenantName?.trim() || (driveName ? t('driveView.title', { tenant: driveName }) : t('driveView.rootFolder')), ...path].join('  /  ')}</span>
                </span>
              )}
            </>
          ),
          type: 'warning',
          confirmText: t('driveView.rename.shareDetachConfirm'),
        })
        if (!proceed) return
      }
    } else if (oldName) {
      // Warn on text ↔ binary extension change (non-pointer files).
      const oldExt = oldName.split('.').pop()?.toLowerCase() || ''
      const newExt = newName.split('.').pop()?.toLowerCase() || ''
      if (oldExt !== newExt && !areExtensionsCompatible(oldExt, newExt)) {
        const proceed = await confirm({
          title: 'Change file type?',
          message: `Renaming from .${oldExt} to .${newExt} will change the file format. Binary files (.xlsx, .xls) cannot be opened as text files and vice versa.`,
          type: 'warning',
          confirmText: 'Rename Anyway',
        })
        if (!proceed) return
      }
    }

    try {
      if (oldName && oldName !== newName) {
        const at = captureUndoContext()   // D134: before the rename's request
        await filesApi.moveFile(id, { name: newName })
        undo.recordRename(at, id, oldName, newName)
      }
      // Refresh the whole tree (rename may affect any level, merge preserves subtrees)
      loadWorkspaceFiles()
      // Open newly created file in editor
      if (wasNewFile) onFileOpen(id)
    } catch (err) {
      console.error('[WorkspaceExplorer] Rename failed:', err)
    }
  }, [loadWorkspaceFiles, undo, confirm, onFileOpen])

  const handleRenameCancel = useCallback(() => {
    const pendingItem = pendingNewItemRef.current
    setRenamingId(null)
    setRenamingScope(null)
    renamingOldNameRef.current = null
    containerRef.current?.focus()
    // If cancelling rename on a newly created item, delete it
    if (pendingItem) {
      pendingNewItemRef.current = null
      // Inline, not the delete job: a just-created item is empty, never a big tree (#921).
      filesApi.deleteFile(pendingItem.id).then(() => loadWorkspaceFiles()).catch(() => {})
    }
  }, [loadWorkspaceFiles])

  const handleRenameFromMenu = useCallback(() => {
    if (contextMenu?.itemId) {
      startRename(contextMenu.itemId, contextMenu.scope, contextMenu.itemName)
    }
  }, [contextMenu, startRename])

  // Type-ahead search buffer: accumulates characters typed in quick succession
  const typeAheadBuffer = useRef('')
  const typeAheadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keyboard shortcuts (scoped to when explorer has focus)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (!containerRef.current?.contains(document.activeElement) && containerRef.current !== document.activeElement) return

      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault()
        filterInputRef.current?.focus()
        filterInputRef.current?.select()
        return
      } else if (e.key === 'F2' && canWrite && selection.size === 1 && !renamingId) {
        e.preventDefault()
        const [id, meta] = [...selection.entries()][0]
        startRename(id, meta.scope, meta.name)
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        // Select all siblings at the same level as the first selected item
        const next = new Map<string, SelectionEntry>()
        if (selection.size > 0) {
          const firstId = [...selection.keys()][0]
          const firstEl = containerRef.current?.querySelector(`[data-file-id="${firstId}"]`)
          const parent = firstEl?.parentElement
          if (parent) {
            const siblings = parent.querySelectorAll(':scope > [data-file-id]')
            for (const el of siblings) {
              const itemId = el.getAttribute('data-file-id')!
              const meta = getItemMetaFromDOM(itemId)
              if (meta) next.set(itemId, meta)
            }
          }
        }
        if (next.size > 0) setSelection(next)
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        // #900 — where a command registry dispatches keys (the workspace on screen), Cmd+C is its `edit.copy` (this
        // same handleCopy), with its rule for highlighted text: a highlight in this pane is the browser's copy, a
        // stale one elsewhere doesn't take the files' copy away. Standing aside keeps ONE rule — deferring here on
        // any highlight, without claiming the key, let the registry copy the files over text this handler meant to
        // leave to the browser.
        if (cmdRegistry?.dispatchesKeys) return
        // Elsewhere: defer to the browser's native copy when the user has real
        // text highlighted — copy is non-destructive, so a selection always wins.
        const sel = window.getSelection()
        if (sel && !sel.isCollapsed && sel.toString()) return
        if (canCopy) { e.preventDefault(); handleCopy() }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        // #900 — as Cmd+C: where a registry dispatches keys, its `edit.cut` (this same handleCut) decides, so a
        // highlight in this pane stays the browser's.
        if (cmdRegistry?.dispatchesKeys) return
        if (canWrite) { e.preventDefault(); handleCut() }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (canWrite) { e.preventDefault(); handlePaste() }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (canWrite && selection.size > 0) {
          e.preventDefault()
          handleDelete()
        }
      } else if (e.key === 'Escape') {
        setSelection(new Map())
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const allIds = getVisibleItemIds()
        if (allIds.length === 0) return
        // Find the currently focused item (last selected)
        const currentId = lastSelectedId.current
        const currentIdx = currentId ? allIds.indexOf(currentId) : -1
        let nextIdx: number
        if (e.key === 'ArrowDown') {
          nextIdx = currentIdx < 0 ? 0 : Math.min(currentIdx + 1, allIds.length - 1)
        } else {
          nextIdx = currentIdx < 0 ? 0 : Math.max(currentIdx - 1, 0)
        }
        const nextId = allIds[nextIdx]
        const meta = getItemMetaFromDOM(nextId)
        if (meta) {
          setSelection(new Map([[nextId, meta]]))
          lastSelectedId.current = nextId
          // Scroll into view
          containerRef.current?.querySelector(`[data-file-id="${nextId}"]`)?.scrollIntoView({ block: 'nearest' })
        }
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (selection.size !== 1) return
        e.preventDefault()
        const [id] = [...selection.keys()]
        const meta = getItemMetaFromDOM(id)
        if (!meta) return
        // Click the DOM element: a folder toggles, a file opens — through the row's own click, so
        // a chain that Needs approval is stopped by the same gate (I114).
        const el = containerRef.current?.querySelector(`[data-file-id="${id}"]`) as HTMLElement | null
        if (el) el.click()
        else if (!meta.isFolder) onFileOpen(id)
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Type-ahead: accumulate characters and jump to matching file
        e.preventDefault()
        if (typeAheadTimer.current) clearTimeout(typeAheadTimer.current)
        typeAheadBuffer.current += e.key.toLowerCase()
        typeAheadTimer.current = setTimeout(() => { typeAheadBuffer.current = '' }, 500)

        const allIds = getVisibleItemIds()
        if (allIds.length === 0) return
        const currentId = lastSelectedId.current
        const currentIdx = currentId ? allIds.indexOf(currentId) : -1

        const jumpTo = (query: string, startIdx: number) => {
          for (let i = 0; i < allIds.length; i++) {
            const idx = (startIdx + i) % allIds.length
            const meta = getItemMetaFromDOM(allIds[idx])
            if (meta && meta.name.toLowerCase().startsWith(query)) {
              setSelection(new Map([[allIds[idx], meta]]))
              lastSelectedId.current = allIds[idx]
              containerRef.current?.querySelector(`[data-file-id="${allIds[idx]}"]`)?.scrollIntoView({ block: 'nearest' })
              return true
            }
          }
          return false
        }

        const query = typeAheadBuffer.current
        if (query.length === 1) {
          // Single char: cycle from after current selection
          jumpTo(query, currentIdx + 1)
        } else {
          // Multi-char: search from top; if no match, reset buffer to last char and try as single
          if (!jumpTo(query, 0)) {
            typeAheadBuffer.current = e.key.toLowerCase()
            jumpTo(typeAheadBuffer.current, currentIdx + 1)
          }
        }
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [selection, renamingId, startRename, handleCopy, handleCut, handlePaste, handleDelete, getVisibleItemIds, getItemMetaFromDOM, onFileOpen, cmdRegistry])

  // ─── Drag and Drop handlers ─────────────────────────────────────────

  const handleDragStart = useCallback((e: React.DragEvent, item: { id: string; name: string; isFolder: boolean }) => {
    // If dragged item is in selection, drag all selected; otherwise drag only this item
    let dragItems: { id: string; name: string; isFolder: boolean }[]
    if (selection.has(item.id)) {
      dragItems = [...selection.entries()].map(([id, m]) => ({ id, name: m.name, isFolder: m.isFolder }))
    } else {
      dragItems = [{ id: item.id, name: item.name, isFolder: item.isFolder }]
    }

    // Alt held at the start adds one file's /file/<id> link (#750's hidden gesture).
    startPapanDrag(e.dataTransfer, { tenantId, files: dragItems }, { link: e.altKey })
    const folderIds = dragItems.filter(d => d.isFolder).map(d => d.id)
    // …and to an app it is dropped on, as content (#605).
    offerDragToApps(e.dataTransfer, dragItems.map(d => d.id))
    const dragEl = createDragImage(dragItems.length === 1 ? [{ name: dragItems[0].name }] : [{ name: `${dragItems.length} items` }])
    document.body.appendChild(dragEl)
    e.dataTransfer.setDragImage(dragEl, 0, 0)
    requestAnimationFrame(() => document.body.removeChild(dragEl))
    setDraggedIds(new Set(dragItems.map(d => d.id)))
    if (folderIds.length > 0) {
      setActiveDragFolderIds(folderIds)
    }
  }, [tenantId, selection])

  const handleItemDragOver = useCallback((e: React.DragEvent, targetItem: { id: string; isFolder: boolean }) => {
    if (!targetItem.isFolder) return

    // Collect all dragged folder IDs
    const allDragIds = new Set(peekPapanDragFolderIds(e.dataTransfer))
    for (const id of draggedIds) allDragIds.add(id)

    if (allDragIds.size > 0) {
      // Centralized circular check: is any dragged folder the target or an ancestor?
      if (isCircularMove(allDragIds, targetItem.id, wsAncestorIds)) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'none'
        setForbiddenDropId(targetItem.id)
        setDropTargetId(null)
        return
      }
      // Also check within workspace files tree (for same-tree descendant detection)
      for (const fId of allDragIds) {
        if (findInTree(workspaceFiles, fId, targetItem.id)) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'none'
          setForbiddenDropId(targetItem.id)
          setDropTargetId(null)
          return
        }
      }
    }

    setForbiddenDropId(null)
    e.preventDefault()
    e.stopPropagation()
    setDropTargetId(targetItem.id)
    e.dataTransfer.dropEffect = (allDragIds.size > 0 || isPapanDrag(e.dataTransfer)) ? 'move' : 'copy'
  }, [draggedIds, workspaceFiles, wsAncestorIds])

  // Simplified dragover for linked tree items — LinkedFolderTree already does its own
  // circular move detection, so we skip wsAncestorIds check (which would incorrectly
  // block drops when the workspace .workpapan folder is an ancestor of the tree root).
  const handleLinkedTreeDragOver = useCallback((e: React.DragEvent, targetItem: { id: string; isFolder: boolean }) => {
    if (!targetItem.isFolder) return
    setForbiddenDropId(null)
    e.preventDefault()
    e.stopPropagation()
    setDropTargetId(targetItem.id)
    const allDragIds = new Set(peekPapanDragFolderIds(e.dataTransfer))
    for (const id of draggedIds) allDragIds.add(id)
    e.dataTransfer.dropEffect = (allDragIds.size > 0 || isPapanDrag(e.dataTransfer)) ? 'move' : 'copy'
  }, [draggedIds])

  const handleForbiddenDragOver = useCallback((targetId: string) => {
    setForbiddenDropId(targetId)
    setDropTargetId(null)
  }, [])

  const handleItemDragLeave = useCallback((_e: React.DragEvent) => {
    setDropTargetId(null)
    setForbiddenDropId(null)
  }, [])

  const executeDrop = useCallback(async (e: React.DragEvent, targetFolderId: string | null, preTransfer?: PapanDragPayload) => {
    setDropTargetId(null)
    setDraggedIds(new Set())

    const transfer = preTransfer ?? readPapanDrag(e.dataTransfer)
    if (!transfer) return

    // Pass real folder ID directly to API (root folder IDs are deterministic: papan_${tenantId})
    const apiTargetId = targetFolderId

    // #1316 review round 2 (owner, 2026-10-28): the move the person started FINISHES as the identity that started it —
    // every request is sent as it (`batch.opts()`); what it SHOWS (toasts, progress, an undo record) stops with its session
    const batch = pinnedBatch()
    const at = captureUndoContext()   // D134: the batch's identity — every item's requests are sent as it
    const shown = () => !batch.ended()
    // the item's parent: from the session's cache while it lasts, else asked as the batch's identity
    const parentOf = async (id: string): Promise<string | null> => {
      if (shown()) {
        try { return (await fetchAndCacheFileInfo(queryClient, id)).parentId }
        catch (err) { if (shown()) throw err }
      }
      return (await filesApi.getFileInfo(id, batch.opts())).parentId
    }
    try {
      if (transfer.tenantId !== tenantId) return

      const movedIds: string[] = []

      for (const file of transfer.files) {
        if (file.id === targetFolderId) continue

        // Client-side circular check on loaded tree
        if (file.isFolder && targetFolderId) {
          if (findInTree(workspaceFiles, file.id, targetFolderId)) {
            if (shown()) toast.error('Cannot move a folder into its own subfolder')
            return
          }
        }

        try {
          const info = { parentId: await parentOf(file.id) }
          // Skip if already in the target folder
          if (info.parentId === apiTargetId) continue
          const r = await withMoveProgress(toast, t('driveView.toast.moving', { name: file.name }), (hooks) => filesApi.moveFile(file.id, { parentId: apiTargetId }, { ...hooks, ...batch.opts() }), batch.ended)
          if (r?.chainEscape || r?.crossTenant || r?.accessChange) {
            // Same guards as cut-paste — surface rather than silently no-op
            // (the workspace explorer doesn't host the resolving dialogs).
            if (shown()) toast.error(r?.error || `"${file.name}" can't be moved here — do it from the main drive.`)
            continue
          }
          if (r?.copied) {
            // Out of a received `.shared`: the server COPIED (source untouched).
            // Say so, and record a copy-undo — never a move-undo.
            const ids = r.copiedItems?.map((x) => x.id) ?? (r.file?.id ? [r.file.id] : [])
            if (ids.length > 0) undo.recordCopy(at, ids)
            if (shown()) toast.success(`Copied "${file.name}"`)
            if (r.copyErrors?.length && shown()) toast.error(`"${file.name}" was only partly copied: ${r.copyErrors[0].message}`)
            continue
          }
          if (!(r.split && isMountId(file.id))) undo.recordMoveResult(at, file.id, info.parentId, file.name, r)
          // #801: a split left the root (and its protected branches) where it was —
          // it isn't "moved", so its links stay too.
          const splitMsg = moveResultMessage(t, r)
          if (splitMsg) { if (shown()) toast.info(`${file.name}: ${splitMsg}`); continue }
          if (shown()) toast.success(`Moved "${file.name}"`)
          movedIds.push(file.id)
        } catch (err: unknown) {
          // The ONE move-error text (a #801 refusal, a lost big-move job's "couldn't confirm", the server's reason).
          if (shown()) toast.error(`${file.name}: ${moveErrorText(err, t)}`)
        }
      }
      // the batch finished as its starter — the workspace view after its session is not touched (a redundant link stays)
      if (!shown()) return

      // Auto-remove links whose target was moved into the workspace
      for (const link of links) {
        if (movedIds.includes(link.targetFolderId)) {
          onRemoveLink(link.id)
        }
      }

      loadWorkspaceFiles()
    } catch {
      // Invalid transfer data
    }
  }, [tenantId, workspaceFiles, loadWorkspaceFiles, toast, t, links, onRemoveLink, undo, queryClient])

  const handleItemDrop = useCallback(async (e: React.DragEvent, targetItem: { id: string; isFolder: boolean }) => {
    e.preventDefault()
    e.stopPropagation()
    if (!targetItem.isFolder) return

    if (isPapanDrag(e.dataTransfer)) {
      await executeDrop(e, targetItem.id)
      return
    }

    setDropTargetId(null)
    setDraggedIds(new Set())
    await upload.handleExternalDrop(e, targetItem.id, resolveConflict)
  }, [executeDrop, upload, resolveConflict])

  const handleSectionDrop = useCallback(async (e: React.DragEvent, targetFolderId: string | null) => {
    e.preventDefault()
    e.stopPropagation()

    // Capture the drop synchronously — browsers detach the DataTransfer after
    // the event handler returns, and we may await onEnsureWorkspace() first; a
    // Papan drag's payload is read now too (its source clears it on dragend).
    // collectDroppedFiles() runs its webkitGetAsEntry() pass before its first
    // await, so the entries (including directories, for recursion) are captured
    // now; we await the resolved snapshot inside doDrop. Reading
    // dataTransfer.files instead would queue directories as empty File objects
    // and the upload would fail (see issue #49). The error handler is attached
    // here, not after the onEnsureWorkspace await, so a rejected read can never
    // sit unhandled; on failure it surfaces a toast and resolves to null.
    const isPapan = isPapanDrag(e.dataTransfer)
    const transfer = isPapan ? readPapanDrag(e.dataTransfer) : null
    const collectPromise = isPapan ? null : collectDroppedFiles(e).catch((err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to process drop')
      return null
    })

    const doDrop = async () => {
      let effectiveTarget = targetFolderId
      if (!effectiveTarget) {
        effectiveTarget = await onEnsureWorkspace?.() ?? null
        if (!effectiveTarget) return
      }

      if (isPapan) {
        if (transfer) await executeDrop(e, effectiveTarget, transfer)
        else { setDropTargetId(null); setDraggedIds(new Set()) }
        return
      }

      setDropTargetId(null)
      setDraggedIds(new Set())
      // collectPromise is non-null here (not a Papan drag); null only if the
      // read failed (the toast was already shown at the catch above).
      const collected = await collectPromise!
      if (!collected) return
      await upload.handleCollectedDrop(collected, effectiveTarget, resolveConflict)
    }

    if (!targetFolderId) {
      await withBusyGuard(doDrop)
    } else {
      await doDrop()
    }
  }, [executeDrop, onEnsureWorkspace, upload, resolveConflict, withBusyGuard, toast])

  const handleSectionDragOver = useCallback((e: React.DragEvent) => {
    if (draggedIds.size === 0 && !isPapanDrag(e.dataTransfer) && !e.dataTransfer.types.includes('Files')) return
    // Circular check: forbid dropping a folder into its own descendant workspace
    if (folderId) {
      const allDragIds = new Set(peekPapanDragFolderIds(e.dataTransfer))
      for (const id of draggedIds) allDragIds.add(id)
      if (allDragIds.size > 0 && isCircularMove(allDragIds, folderId, wsAncestorIds)) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'none'
        setForbiddenDropId('__workspace_root__')
        setDropTargetId(null)
        return
      }
    }
    setForbiddenDropId(null)
    e.preventDefault()
    e.stopPropagation()
    setDropTargetId('__workspace_root__')
    e.dataTransfer.dropEffect = (draggedIds.size > 0 || isPapanDrag(e.dataTransfer)) ? 'move' : 'copy'
  }, [draggedIds, folderId, wsAncestorIds])

  const handleSectionDragLeave = useCallback((_e: React.DragEvent) => {
    setDropTargetId(null)
    setForbiddenDropId(null)
  }, [])

  const handleDragEnd = useCallback(() => {
    setDraggedIds(new Set())
    setDropTargetId(null)
    setForbiddenDropId(null)
    clearActiveDrag()
  }, [])

  // Code-file approval (#1452): the drive's ONE approval flow, for every tree row (context); re-list after an approval or
  // a 409 so every row — the file, and the warnings on the folders above it, at any depth and in every linked tree —
  // shows the server's answer (the permission-change refresh: the root, each loaded folder, each linked tree)
  // (never a global list invalidation — CLAUDE.md caching: the refresh re-reads exactly the loaded folders' lists)
  const approveCode = useApproveCode(useCallback(() => {
    loadWorkspaceFiles()
    permRefreshRef.current()
  }, [loadWorkspaceFiles]))

  // D17 / I114 — the trees' clicks ask this gate (context) before opening a chain that Needs approval.
  const { block: blockPendingPointer, dialogs: pendingPointerDialogs } = usePendingPointerGate({
    urlPrefix,
    rootLabel: tenantName?.trim() ? t('driveView.title', { tenant: tenantName.trim() }) : t('driveView.rootFolder'),
    // The approve event doesn't carry the cleared flag — re-list (the linked trees fetch through
    // the same list keys) (review B4).
    onApproved: () => { void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list'] }); loadWorkspaceFiles() },
  })

  return (
    <CodeApprovalProvider value={approveCode}>
    <PendingPointerGateProvider block={blockPendingPointer}>
    <div
      ref={containerRef}
      tabIndex={-1}
      data-workspace-explorer
      // `pb-3`, not the uniform `p-1`: the card this scrolls inside has a
      // 14px corner radius, so a full-width row sitting 4px from the bottom
      // is SLICED by the rounding — which reads as the panel overflowing the
      // window rather than as a list with more below it. The extra bottom
      // padding lets the last row scroll clear of the corner.
      className="flex-1 overflow-auto p-1 pb-3 text-sm flex flex-col outline-none"
      onClick={(e) => {
        const t = e.target as HTMLElement
        if (!t.closest('[data-file-id]') && !t.closest('button') && !t.closest('input')) {
          setSelection(new Map())
        }
      }}
      onContextMenu={handleBackgroundContextMenu}
    >
      {/* Hidden file input for upload — use <label htmlFor> to trigger (Safari blocks .click()) */}
      {/* Safari requires: on-screen, no pointerEvents:none, no display:none, no visibility:hidden */}
      <input
        ref={fileInputRef}
        id="workspace-file-upload"
        type="file"
        multiple
        tabIndex={-1}
        style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
        onChange={handleFileInputChange}
      />
      {/* Folder picker — same handler. Browser sets webkitRelativePath on
          each file so processPickedFiles can mkdir-p server-side. */}
      <input
        ref={folderInputRef}
        id="workspace-folder-upload"
        type="file"
        multiple
        // @ts-expect-error non-standard but widely supported
        webkitdirectory=""
        directory=""
        tabIndex={-1}
        style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
        onChange={handleFileInputChange}
      />

      {/* Filter input */}
      <div className="px-1 pb-1 relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
        <input
          ref={filterInputRef}
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setFilterText('')
              containerRef.current?.focus()
            }
            e.stopPropagation()
          }}
          placeholder="Filter files..."
          className="w-full text-xs themed-text themed-bg-tertiary border themed-border rounded pl-6 pr-6 py-1 outline-none focus:border-brand-500 placeholder:themed-text-muted"
        />
        {filterText && (
          <button
            onClick={() => { setFilterText(''); containerRef.current?.focus() }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 themed-text-muted hover:themed-text"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Linked-from-Drives section */}
      <div className="mb-1">
        <div className="flex items-center px-1 py-1 group/links" onContextMenu={handleSectionContextMenu('links')}>
          <div
            onClick={() => { setLinksExpanded(!linksExpanded); setSelection(new Map()) }}
            className="flex items-center gap-1 flex-1 text-left text-[11px] themed-text-muted uppercase tracking-wider hover:themed-text-secondary select-none"
          >
            {linksExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Linked from Drives
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setShowFolderPicker(true) }}
            className="p-0.5 themed-text-muted hover:themed-text-secondary hover:themed-bg-tertiary rounded transition-colors cursor-pointer"
            data-tooltip="Link Folder"
            aria-label="Link Folder"
          >
            <Link2 size={12} />
          </button>
        </div>
        {linksExpanded && (
          <div className="ml-1">
            {links.map(link => {
              const displayName = link.alias || (link.path ? link.path.split('/').pop() : null) || link.targetFolderId
              if (link.missing) {
                return (
                  <button
                    key={link.id}
                    onClick={() => onMissingLinkClick?.()}
                    className="flex items-center gap-1.5 w-full text-left px-2 py-1 rounded text-sm text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                    title={link.path || link.targetFolderId}
                  >
                    <AlertTriangle size={12} className="shrink-0" />
                    <span className="truncate">{displayName}</span>
                  </button>
                )
              }
              return (
                <LinkedFolderTree
                  key={link.id}
                  linkId={link.id}
                  targetFolderId={link.targetFolderId}
                  displayName={displayName}
                  selectedIds={selectedIds}
                  activeFileId={activeFileId ?? null}
                  autoExpandIds={autoExpandIds}
                  renamingId={renamingId}
                  renamingScope={renamingScope}
                  draggedIds={draggedIds}
                  dropTargetId={dropTargetId}
                  forbiddenDropId={forbiddenDropId}
                  aiOverrideMap={aiOverrideMap}
                  userPermMap={userPermMap}
                  onFileOpen={onFileOpen}
                  onSelect={(id, name, isFolder, e, parentId, parentName) => handleSelect(id, link.id, name, isFolder, e, parentId, parentName)}
                  onContextMenu={(linkId, targetFolderId, folderName, e) =>
                    handleLinkedFolderContextMenu(linkId, targetFolderId, folderName, e,
                      { reshareBlocked: link.reshareBlocked, shareBroken: link.shareBroken })}
                  onItemContextMenu={(item, e) => handleLinkedItemContextMenu(item, e, link.id)}
                  onRenameCommit={handleRenameCommit}
                  onRenameCancel={handleRenameCancel}
                  onDragStart={handleDragStart}
                  onDragOver={handleLinkedTreeDragOver}
                  onForbiddenDragOver={handleForbiddenDragOver}
                  onDragLeave={handleItemDragLeave}
                  onDrop={handleItemDrop}
                  onDragEnd={handleDragEnd}
                  filterText={filterText ? filterText.toLowerCase() || undefined : undefined}
                  refreshTrigger={linkedRefreshTrigger}
                  deepRefreshTrigger={linkedDeepRefreshTrigger}
                />
              )
            })}
            {links.length === 0 && !filterText && (
              <div className="pl-4 py-1 text-xs themed-text-muted">Nothing linked yet</div>
            )}
          </div>
        )}
      </div>

      {/* Workspace Files section */}
      <div
        className={`mb-1 flex-1 flex flex-col ${
          forbiddenDropId === '__workspace_root__'
            ? 'bg-red-600/10 rounded'
            : dropTargetId === '__workspace_root__' || externalDropHighlight
            ? 'bg-green-600/10 rounded'
            : ''
        }`}
        onDragOver={handleSectionDragOver}
        onDragLeave={handleSectionDragLeave}
        onDrop={(e) => handleSectionDrop(e, folderId)}
      >
        <div
          data-file-id={folderId}
          data-file-name={filesSectionName}
          data-file-is-folder="1"
          data-file-parent-id=""
          data-file-scope=""
          className={`flex items-center px-1 py-1 group/files rounded ${
            forbiddenDropId === '__workspace_root__'
              ? 'bg-red-600/20 border border-red-500/50'
              : dropTargetId === '__workspace_root__' || externalDropHighlight
              ? 'bg-green-600/30 border border-green-500'
              : folderId && selection.has(folderId)
              ? 'bg-brand-500/20'
              : ''
          }`}
        >
          <div
            onContextMenu={handleSectionContextMenu('files')}
            onClick={() => { setFilesExpanded(!filesExpanded); setSelection(new Map()) }}
            className={`flex items-center gap-1 flex-1 text-left text-[11px] uppercase tracking-wider select-none cursor-pointer ${
              forbiddenDropId === '__workspace_root__' || dropTargetId === '__workspace_root__' || externalDropHighlight
                ? 'themed-text-secondary'
                : folderId && selection.has(folderId)
                ? 'themed-text'
                : 'themed-text-muted hover:themed-text-secondary'
            }`}
          >
            {filesExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {filesSectionHeader}
          </div>
          <div ref={uploadMenuRef} className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                uploadTargetRef.current = folderId
                setShowUploadMenu((v) => !v)
              }}
              className="p-0.5 themed-text-muted hover:themed-text-secondary hover:themed-bg-tertiary rounded transition-colors cursor-pointer"
              data-tooltip="Upload"
              aria-label="Upload"
            >
              <Upload size={12} />
            </button>
            {showUploadMenu && (
              <div className="absolute right-0 top-full mt-1 themed-bg-tertiary border themed-border-secondary rounded-lg shadow-xl py-1 z-10 min-w-[160px]">
                <button
                  type="button"
                  onClick={() => { setShowUploadMenu(false); fileInputRef.current?.click() }}
                  className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
                >
                  <Upload size={14} />
                  Upload files
                </button>
                <button
                  type="button"
                  onClick={() => { setShowUploadMenu(false); folderInputRef.current?.click() }}
                  className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
                >
                  <FolderUp size={14} />
                  Upload folder
                </button>
              </div>
            )}
          </div>
        </div>
        {filesExpanded && (
          <div
            className={`ml-1 ${forbiddenDropId === '__workspace_root__' ? 'bg-red-600/10 rounded' : dropTargetId === '__workspace_root__' || externalDropHighlight ? 'bg-green-600/10 rounded' : ''}`}
            onDragOver={handleSectionDragOver}
            onDragLeave={(e) => {
              // Only clear if actually leaving the section (not entering a child)
              if (e.currentTarget.contains(e.relatedTarget as Node)) return
              setDropTargetId(null)
            }}
            onDrop={(e) => handleSectionDrop(e, folderId)}
          >
            {loading ? (
              <div className="flex items-center justify-center p-2">
                <Loader2 size={14} className="animate-spin themed-text-muted" />
              </div>
            ) : workspaceFiles.length > 0 ? (
              workspaceFiles.map(item => (
                <WorkspaceFileItem
                  key={item.id}
                  item={item}
                  depth={0}
                  selectedIds={selectedIds}
                  activeFileId={activeFileId ?? null}
                  autoExpandIds={autoExpandIds}
                  renamingId={renamingScope === null ? renamingId : null}
                  draggedIds={draggedIds}
                  dropTargetId={dropTargetId}
                  forbiddenDropId={forbiddenDropId}
                  aiOverrideMap={aiOverrideMap}
                  userPermMap={userPermMap}
                  inheritedUserPerm={roleDefaultPerm}
                  onSelect={(id: string, name: string, isFolder: boolean, e: React.MouseEvent) => handleSelect(id, null, name, isFolder, e)}
                  onFileOpen={onFileOpen}
                  onLoadChildren={handleLoadChildren}
                  onContextMenu={handleItemContextMenu}
                  onRenameCommit={handleRenameCommit}
                  onRenameCancel={handleRenameCancel}
                  onDragStart={handleDragStart}
                  onDragOver={handleItemDragOver}
                  onDragLeave={handleItemDragLeave}
                  onDrop={handleItemDrop}
                  onDragEnd={handleDragEnd}
                  filterText={filterText ? filterText.toLowerCase() || undefined : undefined}
                  filterAnchored={filterText ? filterText.startsWith('/') : undefined}
                  filterPathPrefix="/"
                />
              ))
            ) : (
              <div className="pl-4 py-1 text-xs themed-text-muted">{filterText ? 'No matches' : 'No files yet'}</div>
            )}
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (() => {
        // Faithful port of the inline menu to the shared FileContextMenu — same
        // items, same conditions, same styling. The share-view explorer renders
        // the same component with a smaller item list.
        const items: FileMenuItem[] = []
        // Frozen selection snapshot (see ContextMenuState.selEntries): a mousedown
        // on the portaled menu clears the live `selection`, so gate/label/act on
        // this instead. Fall back to the right-clicked item for older callers.
        const menuSel: Array<[string, SelectionEntry]> = contextMenu.selEntries
          ?? (contextMenu.itemId
            ? [[contextMenu.itemId, { name: contextMenu.itemName ?? '', isFolder: !!contextMenu.itemIsFolder, parentId: contextMenu.itemParentId ?? null, parentName: contextMenu.itemParentName ?? null, scope: contextMenu.scope ?? null }]]
            : [])
        const menuCount = menuSel.length
        if (contextMenu.linkId) {
          items.push({ kind: 'item', icon: Unlink, label: 'Unlink', danger: true, onClick: handleUnlink })
          items.push({ kind: 'separator' })
        }
        if (contextMenu.itemId) {
          if (menuCount === 1) items.push({ kind: 'item', icon: Pencil, label: 'Rename', shortcut: 'F2', onClick: handleRenameFromMenu })
          if (canCopy) items.push({ kind: 'item', icon: Copy, label: menuCount > 1 ? `Copy ${menuCount} items` : 'Copy', shortcut: formatShortcut({ key: 'c', mod: true }), onClick: () => { handleCopy(menuSel); setContextMenu(null) } })
          if (canWrite) items.push({ kind: 'item', icon: Scissors, label: menuCount > 1 ? `Cut ${menuCount} items` : 'Cut', shortcut: formatShortcut({ key: 'x', mod: true }), onClick: () => { handleCut(menuSel); setContextMenu(null) } })
        }
        // Paste targets the FILES section, so it is out of place on the links
        // header for the same reason Upload was. It stays everywhere else: no
        // icon offers it, so removing it would remove the only way to reach it.
        if (canWrite && clipboard && contextMenu.section !== 'links') {
          const target = getPasteTargetName()
          items.push({ kind: 'item', icon: ClipboardPaste, label: <span className="truncate">{target ? `Paste to ${target}` : 'Paste'}</span>, shortcut: formatShortcut({ key: 'v', mod: true }), onClick: handlePaste })
        }
        // Not for a section menu whose paste was suppressed — a separator with
        // nothing above it renders as a rule across the top of the menu.
        if (contextMenu.itemId || (clipboard && contextMenu.section !== 'links')) items.push({ kind: 'separator' })
        if (contextMenu.itemId && canWrite && onClearAIAccess && AI_ACCESS_SUSPENDED) {
          // AI access is SUSPENDED (#976): no limit can be set — only one the selection
          // already carries can be removed (the owner's own choice; the rest are kept).
          const itemId = contextMenu.itemId
          const targetIds = menuCount > 0 ? menuSel.map(([id]) => id) : [itemId]
          const limited = targetIds.filter(id => aiOverrideMap?.has(id))
          const clearAccess = onClearAIAccess
          if (limited.length > 0) {
            items.push({ kind: 'separator' })
            items.push({ kind: 'item', icon: Bot, label: `${t('aiAccess.removeLimit')}${limited.length > 1 ? ` (${limited.length})` : ''}`, onClick: () => { for (const id of limited) clearAccess(id); setContextMenu(null) } })
          }
        } else if (contextMenu.itemId && canWrite && onSetAIAccess && onClearAIAccess) {
          const itemId = contextMenu.itemId
          const currentOverride = menuCount === 1 ? aiOverrideMap?.get(itemId) : undefined
          const targetIds = menuCount > 0 ? menuSel.map(([id]) => id) : [itemId]
          const setAccess = onSetAIAccess
          const clearAccess = onClearAIAccess
          const options = [
            { label: `${!currentOverride && menuCount <= 1 ? '✓ ' : '  '}Full Access`, onClick: () => { for (const id of targetIds) clearAccess(id); setContextMenu(null) } },
            { label: `${currentOverride === 'r' ? '✓ ' : '  '}Read-only`, onClick: () => { for (const id of targetIds) setAccess(id, 'r'); setContextMenu(null) } },
          ]
          if (contextMenu.itemIsFolder) options.push({ label: `${currentOverride === 'hidden' ? '✓ ' : '  '}Hide files within`, onClick: () => { for (const id of targetIds) setAccess(id, 'hidden'); setContextMenu(null) } })
          items.push({ kind: 'separator' })
          items.push({ kind: 'submenu', icon: Bot, label: `AI Access${menuCount > 1 ? ` (${menuCount})` : ''}`, open: aiSubmenuOpen, onOpen: () => setAiSubmenuOpen(true), onClose: () => setAiSubmenuOpen(false), options })
        }
        if (contextMenu.itemId && tenantId.startsWith('o_')) {
          const itemId = contextMenu.itemId
          items.push({ kind: 'item', icon: Users, label: 'Who has access…', onClick: () => { setWhoHasAccessTarget({ id: itemId, name: contextMenu.itemName || '', isFolder: !!contextMenu.itemIsFolder }); setContextMenu(null) } })
        }
        // Share link — the drive has had this and the workspace has not, so the
        // only way to hand a file out from here was to go and find it in the
        // drive first. NOT gated on an org the way "Who has access…" above is:
        // a link is how you reach someone who is not in your org at all, which
        // is exactly the personal-space case.
        //
        // Hidden for the PDApps container, which cannot be shared whole. The
        // drive decides that from a row's full drive-root path; this tree has
        // none, so the NAME is read as if top-level — which catches `pdapps`
        // and cannot catch `drive-root` (that one needs an empty path, and the
        // root is never a row here anyway). The cost is over-blocking a NESTED
        // folder named like a reserved root: a missing menu item, never a share
        // that should not have been offered. Note the server would NOT refuse
        // that nested lookalike — it walks real ancestors — so this is the only
        // place that decision gets made, and it errs towards offering less.
        //
        // Which rows it is offered on, and why, is `shareLinkOffered` — out of
        // this render so it can be tested, which it could not be here.
        //
        // The ROOT row of a "Linked from Drives" section carries no `itemPointer`,
        // so the Needs-approval arm of that gate is inert there — a hole that
        // predates this item ("Reveal in Drive" reads the same field two branches
        // down) and is filed, not fixed. It does not reach THIS item while the
        // root's own share facts are unknown, because the gate refuses outright
        // then; once they are known a pending chain could pass it.
        const shareRowId = contextMenu.itemId
        if (shareRowId && shareLinkOffered(contextMenu)) {
          const itemId = shareRowId
          const itemName = contextMenu.itemName || ''
          const isFolder = !!contextMenu.itemIsFolder
          items.push({ kind: 'item', icon: Link2, label: 'Share link…', onClick: () => {
            setContextMenu(null)
            // Open on the NAME immediately and patch the path in when it
            // arrives: the round trip is a network call, and opening nothing
            // until it returns left the click with no visible effect at all.
            const token = sharePathLookups.current.begin()
            setShareLinkTarget({ id: itemId, name: itemName, isFolder, path: itemName })
            void (async () => {
              try {
                // `filesApi.getFileInfo`, NOT the cached `fetchAndCacheFileInfo`.
                // The cached one was chosen on the grounds that 30s of staleness
                // is cheap for a tooltip; measured, it is worse than that. The
                // realtime move/rename patch merges its payload over the cached
                // detail and that payload has no `path`, so the stale path is
                // KEPT — and the patch resets the entry's age, so the 30s starts
                // again on every move. Dragging a file into another folder and
                // sharing it then showed the folder it came from — whenever its
                // detail was already cached, which the drag handler itself
                // guarantees by reading it before the move; with nothing cached
                // the patch bails and a refetch saves it. A rename showed the new
                // name in the title and the old one at the end of the path, in
                // the same dialog. The delete path a thousand
                // lines up already asks the server directly for the same reason.
                const info = await filesApi.getFileInfo(itemId)
                // `settle` decides whether this answer still belongs to the open
                // dialog — see `createSharePathLookups` for the three ways it
                // does not.
                if (info.path) setShareLinkTarget(t => sharePathLookups.current.settle(t, token, itemId, info.path!))
              } catch { /* the name alone still names it */ }
            })()
          } })
        }
        if (contextMenu.itemId) {
          const itemId = contextMenu.itemId
          items.push({ kind: 'item', label: 'Reveal in Drive', onClick: () => {
            // A chain that Needs approval is revealed beside its siblings, never opened (I114).
            const pending = !!contextMenu.itemPointer && isPendingPointer(contextMenu.itemPointer)
            const parentId = contextMenu.itemIsFolder && !pending ? itemId : contextMenu.itemParentId
            const prefix = window.location.pathname.match(/^(\/~|\/t\/[^/]+)/)?.[1] || '/~'
            const base = parentId ? `${prefix}/folder/${parentId}` : `${prefix}/folder`
            routerNavigate(`${base}?reveal=${itemId}`)
            setContextMenu(null)
          } })
        }
        // Code-file approval (#1452): the same flow as the row's badge
        if (contextMenu.itemCode && !contextMenu.itemCode.isFolder && contextMenu.itemCode.codeNeedsApproval && contextMenu.itemCode.codeCanApprove) {
          const code = contextMenu.itemCode
          items.push({ kind: 'item', icon: AlertTriangle, testId: 'we-context-approve-code', label: t('driveView.code.approve'), onClick: () => { setContextMenu(null); void approveCode(code) } })
        }
        if (contextMenu.itemId && !contextMenu.itemIsFolder) {
          const itemId = contextMenu.itemId
          const pointer = contextMenu.itemPointer
          items.push({ kind: 'item', icon: ExternalLink, label: 'Open in New Tab', onClick: () => { setContextMenu(null); if (pointer && blockPendingPointer(pointer)) return; window.open(`${urlPrefix}/file/${itemId}`, '_blank') } })
        }
        if (canCopy && contextMenu.itemId) {
          const itemId = contextMenu.itemId
          items.push({ kind: 'item', testId: 'we-context-download', label: menuCount > 1 ? `Download ${menuCount} items` : contextMenu.itemIsFolder ? 'Download as Zip' : 'Download', onClick: () => {
            const ids = menuCount > 0 ? menuSel.map(([id]) => id) : [itemId]
            const containsFolder = menuCount > 0 ? menuSel.some(([, e]) => e.isFolder) : !!contextMenu.itemIsFolder
            if (ids.length > 0) filesApi.download(ids, { containsFolder })
            setContextMenu(null)
          } })
        }
        // Edit icon — single `.miniapp` folder, WRITE access; same author-side
        // action the drive menu offers (opens the shared icon editor dialog).
        {
          const eiPerm = userPermMap?.get(contextMenu.itemId ?? '')
          const eiCanWrite = eiPerm ? ['readwrite', 'admin', 'owner'].includes(eiPerm) : !!canWrite
          if (contextMenu.itemId && menuCount === 1 && eiCanWrite && contextMenu.itemIsFolder && isMiniappFolder(contextMenu.itemName ?? '')) {
            const target = { id: contextMenu.itemId, name: contextMenu.itemName ?? '' }
            items.push({ kind: 'item', icon: Palette, label: 'Edit icon', onClick: () => {
              setContextMenu(null)
              const node = findNode(workspaceFiles, target.id)
              if (node) { setIconEditorTarget({ ...target, metadataJson: node.metadataJson }); return }
              // Linked-tree rows aren't in workspaceFiles — fetch the detail so
              // the editor starts from the CURRENT spec, not the name default.
              fetchAndCacheFileInfo(queryClient, target.id)
                .then(info => setIconEditorTarget({ ...target, metadataJson: (info as { metadataJson?: string | null }).metadataJson ?? null }))
                .catch(() => setIconEditorTarget(target))
            } })
          }
        }
        // Debug log — single `.miniapp` folder / loose HTML file, WRITE access
        // only (the log is author/editor material; the API also enforces this).
        {
          const dlPerm = userPermMap?.get(contextMenu.itemId ?? '')
          const dlCanWrite = dlPerm ? ['readwrite', 'admin', 'owner'].includes(dlPerm) : !!canWrite
          if (contextMenu.itemId && menuCount === 1 && dlCanWrite && supportsDebugLog(contextMenu.itemName ?? '', !!contextMenu.itemIsFolder)) {
            const dl = { id: contextMenu.itemId, name: contextMenu.itemName ?? '', isFolder: !!contextMenu.itemIsFolder }
            items.push({ kind: 'item', icon: Bug, label: 'Debug log', onClick: () => { setDebugLogTarget(dl); setContextMenu(null) } })
          }
        }
        if (canWrite && contextMenu.itemId && (!contextMenu.linkId || contextMenu.itemId !== links.find(l => l.id === contextMenu.linkId)?.targetFolderId)) {
          items.push({ kind: 'separator' })
          items.push({ kind: 'item', icon: Trash2, label: menuCount > 1 ? `Delete ${menuCount} items` : 'Delete', shortcut: '⌫', danger: true, onClick: () => { setContextMenu(null); handleDelete(menuSel) } })
        }
        // The create/link/upload tail. THREE shapes, because a menu opened on a
        // section header is answering a different question from one opened on a
        // file.
        //
        // The rule is that a section's menu offers only what acts on THAT
        // section, and only what has no button of its own — each header already
        // carries its primary action as an icon (Link2 on the links section, the
        // upload menu on the files section), so repeating those made the menu
        // long without making anything reachable. What was left was worse than
        // redundant: Upload, Paste and New Folder all target the files section,
        // so from the "Linked from Drives" header every one of them acted on the
        // section next door.
        if (canWrite) {
          if (contextMenu.section === 'links') {
            // Its own action, and nothing else. Kept rather than dropped because
            // the Link2 glyph beside the header is easy to miss, and this is the
            // one thing that belongs to this section.
            items.push({ kind: 'item', icon: Link2, label: 'Link Drive Folder...', onClick: handleIncludeFolder })
          } else if (contextMenu.section) {
            // Files section, or the empty space below the tree — which is the
            // same place. Everything that acts on THIS section: both creates
            // and both uploads. The header's upload icon duplicates the last
            // two, and that is fine — a right-click should not be the one route
            // that cannot do the obvious thing with the section it is on.
            //
            // Guard the separator on the LAST item, not on emptiness: Paste
            // already pushed its own trailing separator, and two in a row
            // render as a double rule.
            if (items.length > 0 && items[items.length - 1].kind !== 'separator') items.push({ kind: 'separator' })
            items.push({ kind: 'item', label: 'New Folder', onClick: handleCreateFolder })
            items.push({ kind: 'item', label: 'New Text File', onClick: handleNewFile })
            items.push({ kind: 'upload', icon: Upload, label: 'Upload files...', htmlFor: 'workspace-file-upload', onClick: () => { uploadTargetRef.current = getEffectiveParent() || folderId; setContextMenu(null) } })
            items.push({ kind: 'upload', label: 'Upload folder...', htmlFor: 'workspace-folder-upload', onClick: () => { uploadTargetRef.current = getEffectiveParent() || folderId; setContextMenu(null) } })
          } else {
            // An ITEM's menu — unchanged. Here the tail is the only way to
            // create or upload without first dismissing the menu.
            items.push({ kind: 'separator' })
            items.push({ kind: 'item', icon: Link2, label: 'Link Drive Folder...', onClick: handleIncludeFolder })
            items.push({ kind: 'item', label: 'New Folder', onClick: handleCreateFolder })
            items.push({ kind: 'item', label: 'New Text File', onClick: handleNewFile })
            items.push({ kind: 'upload', icon: Upload, label: 'Upload files...', htmlFor: 'workspace-file-upload', onClick: () => { uploadTargetRef.current = getEffectiveParent() || folderId; setContextMenu(null) } })
            items.push({ kind: 'upload', label: 'Upload folder...', htmlFor: 'workspace-folder-upload', onClick: () => { uploadTargetRef.current = getEffectiveParent() || folderId; setContextMenu(null) } })
          }
        }
        return (
          <FileContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            menuRef={contextMenuRef}
            testId="workspace-explorer-context-menu"
            items={items}
          />
        )
      })()}

      {/* Folder picker modal */}
      {showFolderPicker && (
        <FolderPickerDialog
          tenantId={tenantId}
          onSelect={handleFolderSelected}
          onClose={() => setShowFolderPicker(false)}
          excludeIds={folderId ? [folderId] : undefined}
        />
      )}

      {/* Who has access — same dialog used by DriveView. */}
      {whoHasAccessTarget && (
        <WhoHasAccessDialog
          folderId={whoHasAccessTarget.id}
          folderName={whoHasAccessTarget.name}
          tenantId={tenantId}
          isFolder={whoHasAccessTarget.isFolder}
          // Personal drives have no roles/teams — hide that tab and default to
          // Outsiders. Everything else opens on People & teams, PDApps included.
          hidePeopleTeams={!tenantId.startsWith('o_')}
          onClose={() => setWhoHasAccessTarget(null)}
        />
      )}

      {shareLinkTarget && (
        <ShareLinksDialog
          fileId={shareLinkTarget.id}
          fileName={shareLinkTarget.name}
          filePath={shareLinkTarget.path}
          isFolder={shareLinkTarget.isFolder}
          isAppEligible={shareKindForFile({ name: shareLinkTarget.name, isFolder: shareLinkTarget.isFolder }) === 'app'}
          onClose={() => setShareLinkTarget(null)}
        />
      )}

      {iconEditorTarget && (
        <MiniappIconEditorDialog file={iconEditorTarget} tenantId={getActiveTenantId() || undefined} onClose={() => setIconEditorTarget(null)} />
      )}
      {debugLogTarget && (
        <DebugLogViewer file={debugLogTarget} onClose={() => setDebugLogTarget(null)} />
      )}
      {pendingPointerDialogs}
    </div>
    </PendingPointerGateProvider>
    </CodeApprovalProvider>
  )
})

function updateNode(items: FileNode[], id: string, updates: Partial<FileNode>): FileNode[] {
  return items.map(item => {
    if (item.id === id) {
      return { ...item, ...updates }
    }
    if (item.children) {
      return { ...item, children: updateNode(item.children, id, updates) }
    }
    return item
  })
}

/**
 * Merge fresh items with previous items:
 * - Uses fresh item data (name, size, etc.)
 * - Keeps old children if the folder had any (prevents React unmount / expanded state loss)
 * - Marks kept children as stale (isLoaded: false) so auto-re-fetch gets fresh data
 */
function mergeChildren(newItems: FileNode[], prevItems: FileNode[]): FileNode[] {
  const prevMap = new Map<string, FileNode>()
  for (const item of prevItems) {
    prevMap.set(item.id, item)
  }
  return newItems.map(item => {
    const prev = prevMap.get(item.id)
    if (!prev) return item
    // A failed load stays failed across a listing merge: the merge runs on every
    // realtime patch of the parent, and clearing it there would re-ask a
    // failing folder once per event (#1058 code review).
    // (Nor does a parent listing re-arm a failed child: a click, a reveal or a
    // resync does — the cost of not re-asking per patch.)
    // A child's own load still in flight stays in flight (`isLoading`), or the
    // row would show idle and ask a second time.
    if (!prev.isFolder) return item
    const keepChildren = !!prev.children?.length
    if (!keepChildren && !prev.loadFailed && !prev.isLoading) return item
    return {
      ...item,
      ...(keepChildren ? { children: prev.children, isLoaded: false } : {}),
      ...(prev.loadFailed ? { loadFailed: true } : {}),
      ...(prev.isLoading ? { isLoading: true } : {}),
    }
  })
}

function findNode(items: FileNode[], id: string): FileNode | null {
  for (const item of items) {
    if (item.id === id) return item
    if (item.children) {
      const found = findNode(item.children, id)
      if (found) return found
    }
  }
  return null
}

/** Check if targetId exists in the loaded subtree of ancestorId */
function findInTree(items: FileNode[], ancestorId: string, targetId: string): boolean {
  for (const item of items) {
    if (item.id === ancestorId && item.children) {
      return hasNode(item.children, targetId)
    }
    if (item.children && findInTree(item.children, ancestorId, targetId)) {
      return true
    }
  }
  return false
}

function hasNode(items: FileNode[], id: string): boolean {
  for (const item of items) {
    if (item.id === id) return true
    if (item.children && hasNode(item.children, id)) return true
  }
  return false
}
